import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Checkbox, Col, Collapse, DatePicker, Descriptions, Dropdown, Form, Input, InputNumber, Modal, Row, Select, Space, Spin, Statistic, Table, Tabs, Tag, Tooltip, Typography, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd';
import {
  AccountBookOutlined,
  ArrowDownOutlined,
  ArrowDownOutlined as MoveDownOutlined,
  ArrowUpOutlined,
  ArrowUpOutlined as MoveUpOutlined,
  BarChartOutlined,
  BankOutlined,
  DownloadOutlined,
  ExportOutlined,
  DollarOutlined,
  EyeOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  ImportOutlined,
  LockOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  RollbackOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SettingOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  FinanceFlowRecord,
  FinanceReportData,
  FinanceReportTab,
  financeFlowTypeText,
  getEditableMeituanSettlementLedger,
  getFinanceReport,
  getFinanceReportExportConfig,
  importEditableMeituanSettlementLedger,
  importMeituanSettlementEntries,
  type EditableMeituanSettlementImportEntry,
  type MeituanSettlementImportEntry,
  saveFinanceReportExportConfig,
} from '../../services/financeReportService';
import {
  serviceBookingOrderService,
  type MeituanOrderQueryInfoExtraPayment,
  type MeituanOrderQueryInfoReceipt,
  type MeituanOrderQueryInfoResponse,
  type MeituanPaymentShareItem,
  type MeituanPaymentSharesResponse,
  type ServiceBookingOrder,
} from '../../services/serviceBookingOrderService';

const { RangePicker } = DatePicker;
const { Title, Paragraph, Text } = Typography;

// Keep the ledger import/export implementation available, but do not expose
// it in the current report toolbar until the statement workflow is enabled.
const showMeituanStatementTools = false;

const settlementCell = (row: Record<string, unknown>, prefix: string): unknown => {
  const normalizedPrefix = prefix.replace(/[（(].*$/, '').trim();
  const key = Object.keys(row).find(column => {
    const normalizedColumn = column.replace(/[（(].*$/, '').trim();
    return column.trim().startsWith(prefix) || normalizedColumn === normalizedPrefix;
  });
  return key ? row[key] : '';
};

const hasSettlementColumn = (row: Record<string, unknown>, prefix: string): boolean => {
  const normalizedPrefix = prefix.replace(/[（(].*$/, '').trim();
  return Object.keys(row).some(column => {
    const normalizedColumn = column.replace(/[（(].*$/, '').trim();
    return column.trim().startsWith(prefix) || normalizedColumn === normalizedPrefix;
  });
};

const settlementNumber = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const normalized = String(value ?? '').replace(/[￥,\s]/g, '').trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const settlementDate = (value: unknown): string => {
  const text = String(value ?? '').trim();
  const match = text.match(/\d{4}-\d{1,2}-\d{1,2}/);
  return match ? dayjs(match[0]).format('YYYY-MM-DD') : '';
};

const settlementDateTime = (value: unknown): string => {
  const text = String(value ?? '').trim();
  const match = text.match(/\d{4}-\d{1,2}-\d{1,2}(?:\s+\d{1,2}:\d{2}:\d{2})?/);
  if (!match) return '';
  const parsed = dayjs(match[0]);
  return parsed.isValid() ? parsed.format(match[0].includes(':') ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD') : '';
};

const tabItems: Array<{ key: FinanceReportTab; label: string }> = [
  { key: 'overview', label: '总览' },
  { key: 'order', label: '订单收入' },
  { key: 'aftersale', label: '售后退款' },
  { key: 'withdraw', label: '服务人员提现' },
  { key: 'frozen', label: '服务流水已结算' },
  { key: 'service', label: '服务流水未结算' },
  { key: 'deposit', label: '保证金' },
  { key: 'providerSettlement', label: '代理商结算流水' },
  { key: 'providerIncome', label: '代理商收入流水' },
];

const financeTabPathMap: Record<FinanceReportTab, string> = {
  overview: 'overview',
  order: 'order',
  aftersale: 'aftersale',
  withdraw: 'withdraw',
  frozen: 'frozen',
  service: 'service',
  deposit: 'deposit',
  providerSettlement: 'provider-settlement',
  providerIncome: 'provider-income',
};

const depositStatusOptions = [
  { label: '全部', value: '' },
  { label: '缴纳保证金', value: 'pay' },
  { label: '退保证金', value: 'refund' },
  { label: '扣除保证金', value: 'deduct' },
  { label: '保证金退还申请', value: 'refund_apply' },
  { label: '退还申请驳回', value: 'refund_reject' },
];

const orderSourceOptions = [
  { label: '全部', value: '' },
  { label: '微信', value: 'wechat' },
  { label: '美团', value: 'meituan' },
  { label: '抖音', value: 'douyin' },
];

const meituanReceiptStatusMap: Record<number, { color: string; text: string }> = {
  1: { color: 'processing', text: '未使用/未过期' },
  2: { color: 'success', text: '已使用' },
  3: { color: 'warning', text: '未使用/已过期' },
  4: { color: 'default', text: '已退款（未核销退款）' },
  6: { color: 'purple', text: '已核销退款' },
};

const meituanOrderStatusMap: Record<number, { color: string; text: string }> = {
  1: { color: 'processing', text: '初始状态' },
  2: { color: 'warning', text: '创建成功/待支付' },
  3: { color: 'default', text: '订单取消' },
  4: { color: 'error', text: '订单终止' },
  5: { color: 'success', text: '订单完成' },
};

const meituanOrderChannelMap: Record<string, string> = {
  mt: '美团',
  dp: '大众点评',
};

const meituanAmountShareTypeMap: Record<number, string> = {
  [-1]: '未定义汇总（不计入）',
  1: '验证金额（仅核对）',
  2: '平台抵用券',
  5: '积分',
  6: '立减',
  8: '商户抵用券',
  10: '美团支付',
  12: '优惠代码',
  15: '美团立减',
  17: '商家立减',
  18: '美团商家立减',
  21: '次卡',
  22: '打折卡',
  23: 'B端美团支付',
  24: '全渠道会员券',
  25: 'POS支付',
  26: '线下认款平台',
  28: '商家折上折',
};

const meituanPaymentShareColumns: ColumnsType<MeituanPaymentShareItem> = [
  { title: '券码', dataIndex: 'receiptCode', width: 150, render: (value: string) => value || '-' },
  { title: '分摊金额', dataIndex: 'amount', width: 110, render: (value: number) => Number(value || 0).toFixed(2) },
  {
    title: '分摊类型',
    dataIndex: 'amountType',
    render: (value: number) => meituanAmountShareTypeMap[Number(value)] || value || '-',
  },
  { title: '平台分摊', dataIndex: 'platformAmount', width: 110, render: (value: string) => value || '0' },
  { title: '商家分摊', dataIndex: 'merchantAmount', width: 110, render: (value: string) => value || '0' },
];

const meituanExtraPaymentTypeMap: Record<number, string> = {
  1: '夜间费',
  2: '补价费',
};

const meituanExtraPaymentStatusMap: Record<number, { color: string; text: string }> = {
  0: { color: 'default', text: '未支付' },
  1: { color: 'success', text: '已支付' },
};

const moneyFormatter = (value?: number) =>
  `￥${Number(value || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatMeituanAmountText = (value?: unknown) => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  const amount = Number(value);
  if (Number.isFinite(amount)) {
    return `￥${amount.toFixed(2)}`;
  }
  return String(value);
};

const formatMeituanFenAmountText = (value?: unknown) => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  const amount = Number(value);
  if (Number.isFinite(amount)) {
    return `￥${(amount / 100).toFixed(2)}`;
  }
  return String(value);
};

const formatMeituanValueText = (value?: unknown) => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  if (typeof value === 'boolean') {
    return value ? '是' : '否';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value).replace(/^"|"$/g, '');
};

const formatMeituanTimestampText = (value?: unknown) => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) {
    return formatMeituanValueText(value);
  }
  const parsed = dayjs(timestamp);
  if (!parsed.isValid()) {
    return formatMeituanValueText(value);
  }
  return parsed.format('YYYY-MM-DD HH:mm:ss');
};

type CouponInfoDisplayItem = {
  couponCode?: string;
  verified?: boolean;
  verifyStatus?: number;
  amount?: number;
};

const normalizeCouponInfoList = (value?: unknown): CouponInfoDisplayItem[] => {
  if (Array.isArray(value)) {
    return value as CouponInfoDisplayItem[];
  }
  if (typeof value !== 'string') {
    return [];
  }
  const text = value.trim();
  if (!text || text === '--') {
    return [];
  }
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed as CouponInfoDisplayItem[] : [];
  } catch {
    return [];
  }
};

const formatCouponVerifyStatusText = (item: CouponInfoDisplayItem) => {
  const status = Number(item.verifyStatus || 0);
  if (meituanReceiptStatusMap[status]) {
    return meituanReceiptStatusMap[status].text;
  }
  if (status) {
    return String(status);
  }
  return item.verified ? '已核销' : '未核销';
};

const formatCouponInfoText = (value?: unknown) => {
  const couponInfo = normalizeCouponInfoList(value);
  if (couponInfo.length === 0) {
    return '';
  }
  return couponInfo
    .map(item => {
      const code = String(item.couponCode || '').trim() || '-';
      const amount = Number(item.amount || 0);
      return `券码：${code} / 状态：${formatCouponVerifyStatusText(item)} / 金额：${moneyFormatter(amount)}`;
    })
    .join('\n');
};

const defaultDateRange = (): [Dayjs, Dayjs] => [dayjs().subtract(1, 'month'), dayjs()];

interface FinanceReportProps {
  tab?: FinanceReportTab;
}

type ExportFieldKey =
  | 'relatedOrderNo'
  | 'subjectName'
  | 'subjectPhone'
  | 'amount'
  | 'status'
  | 'occurredAt'
  | 'remark'
  | 'flowNo'
  | 'title'
  | 'orderSource'
  | 'orderAmount'
  | 'meituanReservationNo'
  | 'meituanOrderNo'
  | 'settlementType'
  | 'settlementRatio'
  | 'settlementAmountType'
  | 'estimatedAmount'
  | 'incomeAt'
  | 'paymentOrderNo'
  | 'couponInfo'
  | 'meituanStatementMarketingFee'
  | 'meituanMerchantMarketingAmount'
  | 'meituanAddPriceDetail'
  | 'tradeNo'
  | 'payChannel'
  | 'payType'
  | 'payScene'
  | 'bizType'
  | 'appCode'
  | 'productName'
  | 'categoryName'
  | 'petName'
  | 'petQuantity'
  | 'addressText'
  | 'serviceDate'
  | 'serviceTimeDesc'
  | 'totalDays'
  | 'unitPrice'
  | 'totalAmount'
  | 'orderStatus'
  | 'payStatus'
  | 'payMethod'
  | 'dispatchMode'
  | 'assignedStaffName'
  | 'assignedStaffPhone'
  | 'orderCreatedAt'
  | 'orderUpdatedAt'
  | 'fulfillmentCompletedAt'
  | 'meituanVerifiedAt'
  | 'meituanVerifiedCode'
  | 'meituanVerifiedAmount'
  | 'meituanVerifiedGrossAmount'
  | 'meituanMerchantFundedAmount'
  | 'meituanVerifiedAmountSource'
  | 'meituanVerifiedCoverage'
  | 'meituanIncomeAmountSource'
  | 'meituanOrderPaidAmount'
  | 'meituanPayDiscountAmount'
  | 'meituanPlatformServiceFee'
  | 'meituanPlatformSubsidy'
  | 'meituanSettlementAmount'
  | 'providerName'
  | 'customerName'
  | 'customerPhone'
  | 'settlementDate'
  | 'withdrawMethod'
  | 'withdrawAccountName'
  | 'withdrawAccountNo';

interface ExportFieldOption {
  key: ExportFieldKey;
  label: string;
  getValue: (record: FinanceFlowRecord) => string | number;
}

type ColumnSizeConfig = Record<string, { width?: number; height?: number }>;

const financeDetailFields: ExportFieldOption[] = [
  { key: 'relatedOrderNo', label: '订单号', getValue: record => record.relatedOrderNo || '' },
  { key: 'subjectName', label: '客户姓名', getValue: record => record.subjectName || '' },
  { key: 'subjectPhone', label: '客户电话', getValue: record => record.subjectPhone || '' },
  { key: 'amount', label: '金额', getValue: record => Number(record.amount || 0) },
  { key: 'status', label: '状态', getValue: record => record.status || '' },
  { key: 'occurredAt', label: '支付时间', getValue: record => record.occurredAt || '' },
  { key: 'remark', label: '备注', getValue: record => record.remark || '' },
  { key: 'flowNo', label: '支付单号', getValue: record => record.flowNo || '' },
  { key: 'title', label: '标题', getValue: record => record.title || '' },
  { key: 'orderSource', label: '订单来源', getValue: record => record.orderSource || '' },
  { key: 'orderAmount', label: '订单金额', getValue: record => Number(record.orderAmount || 0) },
  { key: 'meituanReservationNo', label: '美团预约单号', getValue: record => record.meituanReservationNo || '' },
  { key: 'meituanOrderNo', label: '美团订单号', getValue: record => record.meituanOrderNo || '' },
  { key: 'settlementType', label: '结算类型', getValue: record => record.settlementType || '' },
  { key: 'settlementRatio', label: '比例', getValue: record => Number(record.settlementRatio || 0) },
  { key: 'settlementAmountType', label: '金额类型', getValue: record => record.settlementAmountType || '' },
  { key: 'estimatedAmount', label: '服务人员结算金额', getValue: record => Number(record.estimatedAmount || 0) },
  { key: 'incomeAt', label: '入账时间', getValue: record => record.incomeAt || '' },
  { key: 'paymentOrderNo', label: '支付订单号', getValue: record => record.paymentOrderNo || '' },
  {
    key: 'couponInfo',
    label: '券码信息',
    getValue: record => formatCouponInfoText(record.couponInfo),
  },
  {
    key: 'meituanStatementMarketingFee',
    label: '账单商家营销费（逐券汇总）',
    getValue: record => Number(record.meituanStatementMarketingFee || 0),
  },
  {
    key: 'meituanMerchantMarketingAmount',
    label: '订单级商家营销费（参考）',
    getValue: record => Number(record.meituanMerchantMarketingAmount || 0),
  },
  {
    key: 'meituanAddPriceDetail',
    label: '补差价详情',
    getValue: record => record.meituanAddPriceDetail || '',
  },
  { key: 'tradeNo', label: '第三方交易号', getValue: record => record.tradeNo || '' },
  { key: 'payChannel', label: '支付渠道', getValue: record => record.payChannel || '' },
  { key: 'payType', label: '支付方式', getValue: record => record.payType || '' },
  { key: 'payScene', label: '支付场景', getValue: record => record.payScene || '' },
  { key: 'bizType', label: '业务类型', getValue: record => record.bizType || '' },
  { key: 'appCode', label: '应用标识', getValue: record => record.appCode || '' },
  { key: 'productName', label: '服务项目', getValue: record => record.productName || '' },
  { key: 'categoryName', label: '服务分类', getValue: record => record.categoryName || '' },
  { key: 'petName', label: '宠物名称', getValue: record => record.petName || '' },
  { key: 'petQuantity', label: '宠物数量', getValue: record => Number(record.petQuantity || 0) },
  { key: 'addressText', label: '服务地址', getValue: record => record.addressText || '' },
  { key: 'serviceDate', label: '服务日期', getValue: record => record.serviceDate || '' },
  { key: 'serviceTimeDesc', label: '服务时间', getValue: record => record.serviceTimeDesc || '' },
  { key: 'totalDays', label: '服务天数', getValue: record => Number(record.totalDays || 0) },
  { key: 'unitPrice', label: '订单单价', getValue: record => Number(record.unitPrice || 0) },
  { key: 'totalAmount', label: '订单总额', getValue: record => Number(record.totalAmount || 0) },
  { key: 'orderStatus', label: '订单状态', getValue: record => record.orderStatus || '' },
  { key: 'payStatus', label: '订单支付状态', getValue: record => record.payStatus || '' },
  { key: 'payMethod', label: '订单支付方式', getValue: record => record.payMethod || '' },
  { key: 'dispatchMode', label: '派单方式', getValue: record => record.dispatchMode || '' },
  { key: 'assignedStaffName', label: '服务人员', getValue: record => record.assignedStaffName || '' },
  { key: 'assignedStaffPhone', label: '服务人员电话', getValue: record => record.assignedStaffPhone || '' },
  { key: 'orderCreatedAt', label: '下单时间', getValue: record => record.orderCreatedAt || '' },
  { key: 'orderUpdatedAt', label: '订单更新时间', getValue: record => record.orderUpdatedAt || '' },
  { key: 'fulfillmentCompletedAt', label: '履约完成时间', getValue: record => record.fulfillmentCompletedAt || '' },
  { key: 'meituanVerifiedAt', label: '验券日期', getValue: record => record.meituanVerifiedAt || '' },
  { key: 'meituanVerifiedCode', label: '核销券码', getValue: record => record.meituanVerifiedCode || '' },
  { key: 'meituanVerifiedAmount', label: '核销金额（分摊净额）', getValue: record => Number(record.meituanVerifiedAmount || 0) },
  { key: 'meituanVerifiedGrossAmount', label: '核销金额（分摊原额）', getValue: record => Number(record.meituanVerifiedGrossAmount || 0) },
  { key: 'meituanMerchantFundedAmount', label: '商家承担（分摊）', getValue: record => Number(record.meituanMerchantFundedAmount || 0) },
  { key: 'meituanVerifiedAmountSource', label: '核销金额来源', getValue: record => record.meituanVerifiedAmountSource || '' },
  { key: 'meituanVerifiedCoverage', label: '逐券分摊覆盖', getValue: record => record.meituanVerifiedCoverage || '' },
  { key: 'meituanIncomeAmountSource', label: '收入金额来源', getValue: record => record.meituanIncomeAmountSource || '' },
  { key: 'meituanOrderPaidAmount', label: '美团订单实付金额', getValue: record => Number(record.meituanOrderPaidAmount || 0) },
  { key: 'meituanPayDiscountAmount', label: '美团支付优惠金额', getValue: record => Number(record.meituanPayDiscountAmount || 0) },
  { key: 'meituanPlatformServiceFee', label: '美团平台技术服务费', getValue: record => Number(record.meituanPlatformServiceFee || 0) },
  { key: 'meituanPlatformSubsidy', label: '美团平台分摊补贴', getValue: record => Number(record.meituanPlatformSubsidy || 0) },
  { key: 'meituanSettlementAmount', label: '美团实际收入（结算价）', getValue: record => Number(record.meituanSettlementAmount || 0) },
  { key: 'providerName', label: '服务人员姓名', getValue: record => record.providerName || record.subjectName || '' },
  { key: 'customerName', label: '客户姓名', getValue: record => record.customerName || '' },
  { key: 'customerPhone', label: '客户电话', getValue: record => record.customerPhone || '' },
  { key: 'settlementDate', label: '结算日期', getValue: record => record.settlementDate || '' },
  { key: 'withdrawMethod', label: '提现方式', getValue: record => record.withdrawMethod || '' },
  { key: 'withdrawAccountName', label: '收款姓名', getValue: record => record.withdrawAccountName || '' },
  { key: 'withdrawAccountNo', label: '收款账号', getValue: record => record.withdrawAccountNo || '' },
];

const defaultOrderIncomeExportFieldKeys: ExportFieldKey[] = [
  'relatedOrderNo',
  'orderSource',
  'meituanReservationNo',
  'meituanOrderNo',
  'meituanVerifiedAt',
  'meituanVerifiedCode',
  'meituanVerifiedAmount',
  'meituanVerifiedGrossAmount',
  'meituanMerchantFundedAmount',
  'meituanVerifiedAmountSource',
  'meituanVerifiedCoverage',
  'meituanIncomeAmountSource',
  'meituanOrderPaidAmount',
  'meituanPayDiscountAmount',
  'couponInfo',
  'meituanStatementMarketingFee',
  'meituanMerchantMarketingAmount',
  'meituanPlatformServiceFee',
  'meituanPlatformSubsidy',
  'meituanSettlementAmount',
  'subjectName',
  'subjectPhone',
  'categoryName',
  'productName',
  'amount',
  'status',
  'occurredAt',
  'serviceDate',
  'serviceTimeDesc',
  'title',
];

const defaultOrderIncomeColumnFieldKeys: ExportFieldKey[] = [
  'relatedOrderNo',
  'orderSource',
  'productName',
  'fulfillmentCompletedAt',
  'meituanVerifiedAt',
  'meituanVerifiedCode',
  'meituanVerifiedAmount',
  'meituanVerifiedGrossAmount',
  'meituanMerchantFundedAmount',
  'meituanVerifiedAmountSource',
  'meituanVerifiedCoverage',
  'meituanIncomeAmountSource',
  'couponInfo',
  'meituanStatementMarketingFee',
  'meituanMerchantMarketingAmount',
  'meituanAddPriceDetail',
  'amount',
  'status',
  'subjectName',
  'subjectPhone',
  'occurredAt',
];

const requiredOrderIncomeExportFieldKeys: ExportFieldKey[] = [
  'meituanStatementMarketingFee',
  'meituanMerchantMarketingAmount',
  'meituanVerifiedAmountSource',
  'meituanVerifiedCoverage',
];

const requiredOrderIncomeColumnFieldKeys: ExportFieldKey[] = [
  'fulfillmentCompletedAt',
  'meituanStatementMarketingFee',
  'meituanMerchantMarketingAmount',
  'meituanVerifiedAmountSource',
  'meituanVerifiedCoverage',
];

const orderIncomeFieldKeys: ExportFieldKey[] = [
  'relatedOrderNo',
  'orderSource',
  'meituanReservationNo',
  'meituanOrderNo',
  'couponInfo',
  'meituanStatementMarketingFee',
  'meituanMerchantMarketingAmount',
  'meituanAddPriceDetail',
  'subjectName',
  'subjectPhone',
  'amount',
  'status',
  'occurredAt',
  'title',
  'categoryName',
  'productName',
  'petName',
  'petQuantity',
  'addressText',
  'serviceDate',
  'serviceTimeDesc',
  'totalDays',
  'unitPrice',
  'totalAmount',
  'orderStatus',
  'payStatus',
  'payMethod',
  'dispatchMode',
  'assignedStaffName',
  'assignedStaffPhone',
  'orderCreatedAt',
  'orderUpdatedAt',
  'fulfillmentCompletedAt',
  'meituanVerifiedAt',
  'meituanVerifiedCode',
  'meituanVerifiedAmount',
  'meituanVerifiedGrossAmount',
  'meituanMerchantFundedAmount',
  'meituanVerifiedAmountSource',
  'meituanVerifiedCoverage',
  'meituanIncomeAmountSource',
  'meituanOrderPaidAmount',
  'meituanPayDiscountAmount',
  'meituanPlatformServiceFee',
  'meituanPlatformSubsidy',
  'meituanSettlementAmount',
  'remark',
];

const defaultAftersaleRefundExportFieldKeys: ExportFieldKey[] = [
  'flowNo',
  'relatedOrderNo',
  'subjectName',
  'subjectPhone',
  'amount',
  'status',
  'occurredAt',
  'title',
  'payChannel',
  'remark',
];

const defaultAftersaleRefundColumnFieldKeys: ExportFieldKey[] = [
  'flowNo',
  'relatedOrderNo',
  'subjectName',
  'subjectPhone',
  'amount',
  'status',
  'occurredAt',
];

const defaultWithdrawExportFieldKeys: ExportFieldKey[] = [
  'flowNo',
  'providerName',
  'subjectPhone',
  'amount',
  'status',
  'occurredAt',
  'withdrawMethod',
  'withdrawAccountName',
  'withdrawAccountNo',
  'remark',
];

const defaultWithdrawColumnFieldKeys: ExportFieldKey[] = [
  'flowNo',
  'providerName',
  'subjectPhone',
  'amount',
  'status',
  'occurredAt',
  'withdrawMethod',
];

const defaultSettlementFlowExportFieldKeys: ExportFieldKey[] = [
  'relatedOrderNo',
  'title',
  'providerName',
  'subjectPhone',
  'customerName',
  'customerPhone',
  'amount',
  'status',
  'occurredAt',
  'serviceDate',
  'settlementDate',
  'remark',
];

const defaultSettlementFlowColumnFieldKeys: ExportFieldKey[] = [
  'amount',
  'status',
  'customerPhone',
  'relatedOrderNo',
  'occurredAt',
  'remark',
  'title',
  'serviceDate',
  'settlementDate',
  'providerName',
  'subjectPhone',
  'customerName',
];

const defaultProviderIncomeFlowFieldKeys: ExportFieldKey[] = [
  'amount',
  'status',
  'relatedOrderNo',
  'orderSource',
  'meituanReservationNo',
  'orderAmount',
  'settlementType',
  'settlementAmountType',
  'settlementRatio',
  'estimatedAmount',
  'settlementDate',
  'incomeAt',
  'providerName',
  'subjectPhone',
];

const defaultDepositExportFieldKeys: ExportFieldKey[] = [
  'flowNo',
  'providerName',
  'subjectPhone',
  'amount',
  'status',
  'occurredAt',
  'remark',
];

const defaultDepositColumnFieldKeys: ExportFieldKey[] = [
  'flowNo',
  'providerName',
  'subjectPhone',
  'amount',
  'status',
  'occurredAt',
];

const orderIncomeReportKey = 'order_income';
const orderIncomeColumnReportKey = 'order_income_columns';
const aftersaleRefundReportKey = 'aftersale_refund';
const aftersaleRefundColumnReportKey = 'aftersale_refund_columns';
const withdrawReportKey = 'service_provider_withdraw';
const withdrawColumnReportKey = 'service_provider_withdraw_columns';
const settledServiceFlowReportKey = 'service_flow_settled';
const settledServiceFlowColumnReportKey = 'service_flow_settled_columns';
const unsettledServiceFlowReportKey = 'service_flow_unsettled';
const unsettledServiceFlowColumnReportKey = 'service_flow_unsettled_columns';
const depositReportKey = 'service_provider_deposit';
const depositColumnReportKey = 'service_provider_deposit_columns';
const providerSettlementReportKey = 'user_provider_settlement';
const providerSettlementColumnReportKey = 'user_provider_settlement_columns';
const providerIncomeReportKey = 'user_provider_income';
const providerIncomeColumnReportKey = 'user_provider_income_columns';

const getExportReportKey = (tab: ConfigurableFinanceTab) => {
  if (tab === 'aftersale') return aftersaleRefundReportKey;
  if (tab === 'withdraw') return withdrawReportKey;
  if (tab === 'frozen') return settledServiceFlowReportKey;
  if (tab === 'service') return unsettledServiceFlowReportKey;
  if (tab === 'deposit') return depositReportKey;
  if (tab === 'providerSettlement') return providerSettlementReportKey;
  if (tab === 'providerIncome') return providerIncomeReportKey;
  return orderIncomeReportKey;
};

const getColumnReportKey = (tab: ConfigurableFinanceTab) => {
  if (tab === 'aftersale') return aftersaleRefundColumnReportKey;
  if (tab === 'withdraw') return withdrawColumnReportKey;
  if (tab === 'frozen') return settledServiceFlowColumnReportKey;
  if (tab === 'service') return unsettledServiceFlowColumnReportKey;
  if (tab === 'deposit') return depositColumnReportKey;
  if (tab === 'providerSettlement') return providerSettlementColumnReportKey;
  if (tab === 'providerIncome') return providerIncomeColumnReportKey;
  return orderIncomeColumnReportKey;
};

const getDefaultExportFieldKeys = (tab: ConfigurableFinanceTab) => {
  if (tab === 'aftersale') return defaultAftersaleRefundExportFieldKeys;
  if (tab === 'withdraw') return defaultWithdrawExportFieldKeys;
  if (tab === 'providerIncome') return defaultProviderIncomeFlowFieldKeys;
  if (tab === 'frozen' || tab === 'service' || tab === 'providerSettlement') return defaultSettlementFlowExportFieldKeys;
  if (tab === 'deposit') return defaultDepositExportFieldKeys;
  return defaultOrderIncomeExportFieldKeys;
};

const getDefaultColumnFieldKeys = (tab: ConfigurableFinanceTab) => {
  if (tab === 'aftersale') return defaultAftersaleRefundColumnFieldKeys;
  if (tab === 'withdraw') return defaultWithdrawColumnFieldKeys;
  if (tab === 'providerIncome') return defaultProviderIncomeFlowFieldKeys;
  if (tab === 'frozen' || tab === 'service' || tab === 'providerSettlement') return defaultSettlementFlowColumnFieldKeys;
  if (tab === 'deposit') return defaultDepositColumnFieldKeys;
  return defaultOrderIncomeColumnFieldKeys;
};

const normalizeFinanceDetailFieldKeys = (
  keys: string[] | undefined,
  defaultKeys: ExportFieldKey[],
  fields: ExportFieldOption[] = financeDetailFields,
): ExportFieldKey[] => {
  const validKeys = new Set(fields.map(item => item.key));
  const result: ExportFieldKey[] = [];
  (keys || []).forEach(key => {
    if (validKeys.has(key as ExportFieldKey) && !result.includes(key as ExportFieldKey)) {
      result.push(key as ExportFieldKey);
    }
  });
  return result.length ? result : defaultKeys;
};

const mergeRequiredFinanceFieldKeys = (
  keys: ExportFieldKey[],
  requiredKeys: ExportFieldKey[],
  fields: ExportFieldOption[] = financeDetailFields,
) => {
  const validKeys = new Set(fields.map(item => item.key));
  const result = [...keys];
  requiredKeys.forEach(key => {
    if (validKeys.has(key) && !result.includes(key)) {
      result.push(key);
    }
  });
  return result;
};

const financeDetailColumnWidthMap: Partial<Record<ExportFieldKey, number>> = {
  relatedOrderNo: 180,
  subjectName: 140,
  subjectPhone: 150,
  amount: 140,
  status: 110,
  occurredAt: 170,
  remark: 220,
  flowNo: 190,
  title: 180,
  orderSource: 120,
  orderAmount: 140,
  meituanReservationNo: 180,
  meituanOrderNo: 180,
  settlementType: 140,
  settlementRatio: 110,
  settlementAmountType: 150,
  estimatedAmount: 170,
  incomeAt: 170,
  paymentOrderNo: 190,
  couponInfo: 360,
  meituanStatementMarketingFee: 180,
  meituanMerchantMarketingAmount: 150,
  meituanAddPriceDetail: 260,
  tradeNo: 220,
  payChannel: 130,
  payType: 130,
  payScene: 130,
  bizType: 130,
  appCode: 130,
  productName: 180,
  categoryName: 150,
  petName: 140,
  petQuantity: 110,
  addressText: 260,
  serviceDate: 140,
  serviceTimeDesc: 220,
  totalDays: 110,
  unitPrice: 130,
  totalAmount: 130,
  orderStatus: 130,
  payStatus: 150,
  payMethod: 150,
  dispatchMode: 130,
  assignedStaffName: 140,
  assignedStaffPhone: 150,
  orderCreatedAt: 170,
  orderUpdatedAt: 170,
  fulfillmentCompletedAt: 170,
  meituanVerifiedAt: 170,
  meituanVerifiedCode: 220,
  meituanVerifiedAmount: 140,
  meituanVerifiedGrossAmount: 160,
  meituanMerchantFundedAmount: 150,
  meituanVerifiedAmountSource: 170,
  meituanVerifiedCoverage: 130,
  meituanIncomeAmountSource: 190,
  meituanOrderPaidAmount: 160,
  meituanPayDiscountAmount: 160,
  meituanPlatformServiceFee: 180,
  meituanPlatformSubsidy: 180,
  meituanSettlementAmount: 180,
};

const renderEmpty = (value: string | number) => {
  if (value === '') {
    return '--';
  }
  return value;
};

const getOrderChartDate = (record: FinanceFlowRecord) => {
  const rawDate = record.occurredAt || record.orderCreatedAt || '';
  return rawDate.slice(0, 10) || '未知日期';
};

const configurableFinanceTabs = ['order', 'aftersale', 'withdraw', 'frozen', 'service', 'deposit', 'providerSettlement', 'providerIncome'] as const;
type ConfigurableFinanceTab = (typeof configurableFinanceTabs)[number];

const isConfigurableFinanceTab = (tab: FinanceReportTab): tab is ConfigurableFinanceTab =>
  configurableFinanceTabs.includes(tab as ConfigurableFinanceTab);

const aftersaleRefundFieldLabelMap: Partial<Record<ExportFieldKey, string>> = {
  flowNo: '退款单号',
  paymentOrderNo: '退款单号',
  tradeNo: '退款交易号',
  amount: '退款金额',
  status: '退款状态',
  occurredAt: '退款时间',
  payChannel: '退款渠道',
};

const withdrawFieldLabelMap: Partial<Record<ExportFieldKey, string>> = {
  flowNo: '提现单号',
  subjectPhone: '服务人员电话',
  amount: '提现金额',
  status: '提现状态',
  occurredAt: '到账时间',
  remark: '状态说明',
};

const settlementFlowFieldLabelMap: Partial<Record<ExportFieldKey, string>> = {
  subjectPhone: '服务人员电话',
  occurredAt: '完成日期',
};

const providerFlowFieldLabelMap: Partial<Record<ExportFieldKey, string>> = {
  providerName: '代理商姓名',
  subjectPhone: '代理商电话',
  relatedOrderNo: '订单号',
  orderSource: '订单来源',
  orderAmount: '订单金额',
  meituanReservationNo: '美团预约单号',
  meituanOrderNo: '美团订单号',
  settlementType: '结算类型',
  settlementRatio: '比例',
  settlementAmountType: '金额类型',
  estimatedAmount: '服务人员结算金额',
  customerName: '客户姓名',
  customerPhone: '客户电话',
  occurredAt: '发生日期',
  settlementDate: '结算日期',
  incomeAt: '入账时间',
};

const orderIncomeFieldLabelMap: Partial<Record<ExportFieldKey, string>> = {
  amount: '收入金额（未扣技术费）',
  meituanVerifiedAt: '验券日期',
  meituanVerifiedCode: '核销券码',
  meituanVerifiedAmount: '核销金额（分摊净额）',
  meituanVerifiedGrossAmount: '核销金额（分摊原额）',
  meituanMerchantFundedAmount: '商家承担（分摊）',
  meituanVerifiedAmountSource: '核销金额来源',
  meituanVerifiedCoverage: '逐券分摊覆盖',
  meituanIncomeAmountSource: '收入金额来源',
  meituanStatementMarketingFee: '账单商家营销费（逐券汇总）',
  meituanMerchantMarketingAmount: '订单级商家营销费（参考）',
  meituanAddPriceDetail: '补差价详情',
};

const depositFieldLabelMap: Partial<Record<ExportFieldKey, string>> = {
  subjectPhone: '电话',
  amount: '保证金金额',
  occurredAt: '日期',
};

const getFinanceDetailFieldLabel = (field: ExportFieldOption, tab: FinanceReportTab) => {
  if (tab === 'aftersale') {
    return aftersaleRefundFieldLabelMap[field.key] || field.label;
  }
  if (tab === 'withdraw') {
    return withdrawFieldLabelMap[field.key] || field.label;
  }
  if (tab === 'frozen' || tab === 'service') {
    return settlementFlowFieldLabelMap[field.key] || field.label;
  }
  if (tab === 'providerSettlement' || tab === 'providerIncome') {
    return providerFlowFieldLabelMap[field.key] || field.label;
  }
  if (tab === 'deposit') {
    return depositFieldLabelMap[field.key] || field.label;
  }
  if (tab === 'order') {
    return orderIncomeFieldLabelMap[field.key] || field.label;
  }
  return field.label;
};

const getFinanceDetailFieldsByTab = (tab: FinanceReportTab) => {
  if (tab === 'order') {
    return financeDetailFields.filter(field => orderIncomeFieldKeys.includes(field.key));
  }
  if (tab === 'aftersale') {
    return financeDetailFields.filter(field => field.key !== 'paymentOrderNo');
  }
  if (tab === 'withdraw') {
    return financeDetailFields.filter(field =>
      [
        'flowNo',
        'providerName',
        'subjectPhone',
        'amount',
        'status',
        'occurredAt',
        'withdrawMethod',
        'withdrawAccountName',
        'withdrawAccountNo',
        'remark',
      ].includes(field.key),
    );
  }
  if (tab === 'frozen' || tab === 'service') {
    return financeDetailFields.filter(field =>
      [
        'relatedOrderNo',
        'title',
        'providerName',
        'subjectPhone',
        'customerName',
        'customerPhone',
        'amount',
        'status',
        'occurredAt',
        'serviceDate',
        'settlementDate',
        'remark',
      ].includes(field.key),
    );
  }
  if (tab === 'providerSettlement') {
    return financeDetailFields.filter(field =>
      ['flowNo', 'providerName', 'subjectPhone', 'amount', 'status', 'occurredAt', 'settlementDate', 'remark'].includes(field.key),
    );
  }
  if (tab === 'providerIncome') {
    return financeDetailFields.filter(field =>
      [
        'amount',
        'status',
        'relatedOrderNo',
        'orderSource',
        'meituanReservationNo',
        'meituanOrderNo',
        'orderAmount',
        'settlementType',
        'settlementAmountType',
        'settlementRatio',
        'estimatedAmount',
        'settlementDate',
        'incomeAt',
        'providerName',
        'subjectPhone',
      ].includes(field.key),
    );
  }
  if (tab === 'deposit') {
    return financeDetailFields.filter(field =>
      ['flowNo', 'providerName', 'subjectPhone', 'amount', 'status', 'occurredAt', 'remark'].includes(field.key),
    );
  }
  return financeDetailFields;
};

const FinanceReport: React.FC<FinanceReportProps> = ({ tab = 'overview' }) => {
  const navigate = useNavigate();
  const activeTab = tab;
  const isConfigurableDetailTab = isConfigurableFinanceTab(activeTab);
  const [orderSearchForm] = Form.useForm();
  const [aftersaleSearchForm] = Form.useForm();
  const [withdrawSearchForm] = Form.useForm();
  const [settlementSearchForm] = Form.useForm();
  const [depositSearchForm] = Form.useForm();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(defaultDateRange());
  const [dateRangeExplicit, setDateRangeExplicit] = useState(false);
  const [orderFilters, setOrderFilters] = useState<{
    customerName?: string;
    customerPhone?: string;
    orderNo?: string;
    orderSource?: string;
    creationModeGroup?: 'manual' | 'automatic' | 'unknown';
    meituanReservationNo?: string;
    meituanOrderNo?: string;
    fulfillmentStartDate?: string;
    fulfillmentEndDate?: string;
    paymentStartDate?: string;
    paymentEndDate?: string;
    verifiedStartDate?: string;
    verifiedEndDate?: string;
    verifiedStatus?: 'verified' | 'unverified';
  }>({});
  const [aftersaleFilters, setAftersaleFilters] = useState<{
    customerName?: string;
    customerPhone?: string;
    orderNo?: string;
  }>({});
  const [withdrawFilters, setWithdrawFilters] = useState<{
    customerName?: string;
    customerPhone?: string;
  }>({});
  const [settlementFilters, setSettlementFilters] = useState<{
    orderNo?: string;
    title?: string;
    providerName?: string;
    providerPhone?: string;
    customerName?: string;
    customerPhone?: string;
  }>({});
  const [depositFilters, setDepositFilters] = useState<{
    customerName?: string;
    customerPhone?: string;
    status?: string;
  }>({});
  const [providerFlowFilters, setProviderFlowFilters] = useState<{
    orderNo?: string;
    providerName?: string;
    providerPhone?: string;
    status?: string;
  }>({});
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<FinanceReportData | null>(null);
  const [reportRefreshKey, setReportRefreshKey] = useState(0);
  const [meituanSettlementImporting, setMeituanSettlementImporting] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportModalMode, setExportModalMode] = useState<'export' | 'config'>('export');
  const [exportLimit, setExportLimit] = useState<number | null>(null);
  const [exportFieldKeys, setExportFieldKeys] = useState<ExportFieldKey[]>(defaultOrderIncomeExportFieldKeys);
  const [exportConfigLoading, setExportConfigLoading] = useState(false);
  const [columnConfigModalOpen, setColumnConfigModalOpen] = useState(false);
  const [columnFieldKeys, setColumnFieldKeys] = useState<ExportFieldKey[]>(defaultOrderIncomeColumnFieldKeys);
  const [columnSizes, setColumnSizes] = useState<ColumnSizeConfig>({});
  const [columnConfigLoading, setColumnConfigLoading] = useState(false);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [statisticsRuleModalOpen, setStatisticsRuleModalOpen] = useState(false);
  const [meituanAmountModalOpen, setMeituanAmountModalOpen] = useState(false);
  const [meituanCouponStatsModalOpen, setMeituanCouponStatsModalOpen] = useState(false);
  const [meituanCouponModalOpen, setMeituanCouponModalOpen] = useState(false);
  const [meituanCouponLoading, setMeituanCouponLoading] = useState(false);
  const [meituanCouponRecord, setMeituanCouponRecord] = useState<FinanceFlowRecord | null>(null);
  const [meituanCouponResult, setMeituanCouponResult] = useState<MeituanOrderQueryInfoResponse | null>(null);
  const [meituanPaymentSharesModalOpen, setMeituanPaymentSharesModalOpen] = useState(false);
  const [meituanPaymentSharesLoading, setMeituanPaymentSharesLoading] = useState(false);
  const [meituanPaymentSharesRecord, setMeituanPaymentSharesRecord] = useState<FinanceFlowRecord | null>(null);
  const [meituanPaymentSharesResult, setMeituanPaymentSharesResult] = useState<MeituanPaymentSharesResponse | null>(null);
  const [orderDetailModalOpen, setOrderDetailModalOpen] = useState(false);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const [orderDetailRecord, setOrderDetailRecord] = useState<FinanceFlowRecord | null>(null);
  const [orderDetailResult, setOrderDetailResult] = useState<ServiceBookingOrder | null>(null);
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const draggingExportKeyRef = useRef<ExportFieldKey | null>(null);
  const draggingColumnKeyRef = useRef<ExportFieldKey | null>(null);
  const activeTabRef = useRef(activeTab);
  const [draggingExportKey, setDraggingExportKey] = useState<ExportFieldKey | null>(null);
  const [draggingColumnKey, setDraggingColumnKey] = useState<ExportFieldKey | null>(null);

  useEffect(() => {
    if (activeTab !== 'order') {
      setOrderDetailModalOpen(false);
      setOrderDetailRecord(null);
      setOrderDetailResult(null);
    }
  }, [activeTab]);

  const handleImportMeituanSettlement: UploadProps['beforeUpload'] = async file => {
    setMeituanSettlementImporting(true);
    try {
      const XLSX = await import('xlsx');
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
      const sheet = workbook.Sheets['收益明细表'] || workbook.Sheets[workbook.SheetNames[0]];
      if (!sheet) {
        message.error('未找到可导入的美团收益明细表');
        return false;
      }
      // 美团收益明细表首行是说明文字，第二行才是列标题。
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '', raw: false, range: 1 });
      const entries: MeituanSettlementImportEntry[] = rows
        .map(row => {
          const statementDate = settlementDate(settlementCell(row, '账单周期')) || settlementDate(settlementCell(row, '验券/退款/调整时间'));
          const externalOrderNo = String(settlementCell(row, '订单号') ?? '').trim();
          const couponCode = String(settlementCell(row, '券号') ?? '').trim();
          const grossIncome = settlementNumber(settlementCell(row, '总收入'));
          return {
            statementDate,
            incomeType: String(settlementCell(row, '收益类型') ?? '').trim(),
            externalOrderNo,
            couponCode,
            verifiedAt: settlementDateTime(settlementCell(row, '验券/退款/调整时间')),
            orderTotalAmount: grossIncome,
            // 收益明细表通常没有“订单实付金额”列；缺失时保持 0，不能以
            // 总收入代替。真实实付金额只能来自美团订单查询接口。
            orderPaidAmount: settlementNumber(settlementCell(row, '订单实付金额')),
            merchantMarketingFee: settlementNumber(settlementCell(row, '商家营销费用')),
            platformServiceFee: settlementNumber(settlementCell(row, '平台技术服务费')),
            platformSubsidy: settlementNumber(settlementCell(row, '平台分摊补贴')),
            otherAdjustment: settlementNumber(settlementCell(row, '其他调整')),
            channelCommission: settlementNumber(settlementCell(row, '渠道佣金')),
            settlementAmount: settlementNumber(settlementCell(row, '结算价')),
            payoutDate: settlementDate(settlementCell(row, '入账日期')),
            payoutStatus: String(settlementCell(row, '打款状态') ?? '').trim(),
            payoutNo: String(settlementCell(row, '打款单号') ?? '').trim(),
          };
        })
        .filter(entry => entry.statementDate && entry.externalOrderNo && entry.couponCode && entry.incomeType);
      if (entries.length === 0) {
        message.error('未读取到有效的美团账单明细，请使用包含订单号、券号、账单周期的收益明细表');
        return false;
      }
      const result = await importMeituanSettlementEntries(entries);
      message.success(`已导入 ${result.imported} 条美团结算明细`);
      setReportRefreshKey(value => value + 1);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '美团结算账单导入失败');
    } finally {
      setMeituanSettlementImporting(false);
    }
    return false;
  };

  const handleExportEditableMeituanSettlementLedger = async () => {
    setMeituanSettlementImporting(true);
    try {
      const [XLSX, ledger] = await Promise.all([
        import('xlsx'),
        getEditableMeituanSettlementLedger(),
      ]);
      const rows = ledger.entries.map(entry => ({
        操作: '更新',
        账本行ID: entry.id,
        行版本: dayjs(entry.updatedAt).valueOf(),
        美团统一订单号: entry.externalOrderNo,
        券码: entry.couponCode,
        账单周期: dayjs(entry.statementDate).format('YYYY-MM-DD'),
        收益类型: entry.incomeType,
        事件时间: entry.verifiedAt ? dayjs(entry.verifiedAt).format('YYYY-MM-DD HH:mm:ss') : '',
        总收入: entry.orderTotalAmount,
        商家营销费: entry.merchantMarketingFee,
        平台技术服务费: entry.platformServiceFee,
        平台分摊补贴: entry.platformSubsidy,
        渠道佣金: entry.channelCommission,
        其他调整: entry.otherAdjustment,
        结算价: entry.settlementAmount,
        订单实付金额: entry.orderPaidAmount,
        变更原因: '',
        导入备注: '',
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows, {
        header: ['操作', '账本行ID', '行版本', '美团统一订单号', '券码', '账单周期', '收益类型', '事件时间', '总收入', '商家营销费', '平台技术服务费', '平台分摊补贴', '渠道佣金', '其他调整', '结算价', '订单实付金额', '变更原因', '导入备注'],
      });
      worksheet['!cols'] = [
        { wch: 10 }, { wch: 22 }, { wch: 16 }, { wch: 24 }, { wch: 18 }, { wch: 13 }, { wch: 12 }, { wch: 20 },
        { wch: 13 }, { wch: 13 }, { wch: 16 }, { wch: 15 }, { wch: 13 }, { wch: 13 }, { wch: 13 }, { wch: 15 }, { wch: 28 }, { wch: 28 },
      ];
      const guide = XLSX.utils.aoa_to_sheet([
        ['可编辑美团券码账本 V2'],
        ['规则', '说明'],
        ['更新', '保留账本行ID和行版本；删除可选列的整列表头表示保持原值，保留的金额列会按单元格数值更新（填 0 即改为零）。'],
        ['新增', '复制行后改为新增，并清空账本行ID、行版本；必须填写统一订单号、券码、账单周期、收益类型、事件时间、总收入、商家营销费、技术服务费、结算价。'],
        ['作废', '保留账本行ID和行版本，填写变更原因；不要通过直接删除行来作废。'],
        ['拆分券码', '将原行作废，再复制为多行新增，分别填写每张券码和金额。'],
        ['金额', '金额保留账单正负号；“收入报表导出”不是本模板，不能直接反向导入。'],
      ]);
      guide['!cols'] = [{ wch: 16 }, { wch: 100 }];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '券码账本V2');
      XLSX.utils.book_append_sheet(workbook, guide, '导入说明');
      XLSX.writeFile(workbook, `美团可编辑券码账本V2_${dayjs().format('YYYYMMDDHHmmss')}.xlsx`);
      message.success(`已导出 ${rows.length} 条可编辑券码账本`);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '导出可编辑券码账本失败');
    } finally {
      setMeituanSettlementImporting(false);
    }
  };

  const handleImportEditableMeituanSettlementLedger: UploadProps['beforeUpload'] = async file => {
    setMeituanSettlementImporting(true);
    try {
      const XLSX = await import('xlsx');
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
      const sheet = workbook.Sheets['券码账本V2'];
      if (!sheet) {
        message.error('未找到“券码账本V2”工作表，请使用系统导出的可编辑模板');
        return false;
      }
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '', raw: false });
      const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
      const requiredHeaders = ['操作', '账本行ID', '行版本', '美团统一订单号', '券码', '账单周期', '收益类型', '事件时间', '总收入', '商家营销费', '平台技术服务费', '结算价', '变更原因'];
      const missingHeaders = requiredHeaders.filter(header => !headers.some(column => column.trim() === header));
      if (missingHeaders.length > 0) {
        message.error(`模板缺少必要列：${missingHeaders.join('、')}`);
        return false;
      }
      const entries = rows.map((row, index): EditableMeituanSettlementImportEntry => ({
        action: String(settlementCell(row, '操作') || '').trim() as EditableMeituanSettlementImportEntry['action'],
        ledgerRowId: String(settlementCell(row, '账本行ID') || '').trim(),
        rowVersion: settlementNumber(settlementCell(row, '行版本')),
        providedFields: [
          ['订单实付金额', 'orderPaidAmount'],
          ['平台分摊补贴', 'platformSubsidy'],
          ['渠道佣金', 'channelCommission'],
          ['其他调整', 'otherAdjustment'],
        ].filter(([column]) => hasSettlementColumn(row, column)).map(([, field]) => field),
        statementDate: settlementDate(settlementCell(row, '账单周期')),
        incomeType: String(settlementCell(row, '收益类型') || '').trim(),
        externalOrderNo: String(settlementCell(row, '美团统一订单号') || '').trim(),
        couponCode: String(settlementCell(row, '券码') || '').trim(),
        verifiedAt: settlementDateTime(settlementCell(row, '事件时间')),
        orderTotalAmount: settlementNumber(settlementCell(row, '总收入')),
        orderPaidAmount: settlementNumber(settlementCell(row, '订单实付金额')),
        merchantMarketingFee: settlementNumber(settlementCell(row, '商家营销费')),
        platformServiceFee: settlementNumber(settlementCell(row, '平台技术服务费')),
        platformSubsidy: settlementNumber(settlementCell(row, '平台分摊补贴')),
        otherAdjustment: settlementNumber(settlementCell(row, '其他调整')),
        channelCommission: settlementNumber(settlementCell(row, '渠道佣金')),
        settlementAmount: settlementNumber(settlementCell(row, '结算价')),
        changeReason: String(settlementCell(row, '变更原因') || '').trim(),
        sourceRowNo: index + 2,
      })).filter(entry => entry.action);
      if (entries.length === 0) {
        message.error('未读取到可导入行，请至少填写一行操作');
        return false;
      }
      const invalid = entries.find(entry => {
        if (entry.action === '作废') return !entry.ledgerRowId || !entry.rowVersion || !entry.changeReason;
        return !entry.statementDate || !entry.incomeType || !entry.externalOrderNo || !entry.couponCode || !entry.verifiedAt ||
          (entry.action === '新增' ? Boolean(entry.ledgerRowId) : !entry.ledgerRowId || !entry.rowVersion || !entry.changeReason);
      });
      if (invalid) {
        message.error('模板存在缺少必要字段或操作不合法的行；请查看“导入说明”后重试');
        return false;
      }
      const result = await importEditableMeituanSettlementLedger(entries);
      message.success(`已处理 ${result.imported} 条可编辑券码账本记录`);
      setReportRefreshKey(value => value + 1);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '可编辑券码账本导入失败');
    } finally {
      setMeituanSettlementImporting(false);
    }
    return false;
  };

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    const tableWrap = tableWrapRef.current;
    if (!tableWrap) return;

    const handleNativeTableWheel = (event: WheelEvent) => {
      if (!['order', 'aftersale', 'withdraw', 'frozen', 'service', 'deposit', 'providerSettlement', 'providerIncome'].includes(activeTabRef.current)) return;

      const scrollContainer = tableWrap.querySelector<HTMLElement>('.ant-table-body, .ant-table-content');
      if (!scrollContainer || scrollContainer.scrollWidth <= scrollContainer.clientWidth) return;

      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (!delta) return;

      event.preventDefault();
      event.stopPropagation();
      scrollContainer.scrollLeft += delta;
    };

    tableWrap.addEventListener('wheel', handleNativeTableWheel, { passive: false });
    return () => {
      tableWrap.removeEventListener('wheel', handleNativeTableWheel);
    };
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await getFinanceReport({
          // The initially displayed range is only a visual default. It must
          // not become a hidden fourth condition for the order filters.
          startDate: activeTab === 'order' && !dateRangeExplicit ? '' : dateRange?.[0].format('YYYY-MM-DD') || '',
          endDate: activeTab === 'order' && !dateRangeExplicit ? '' : dateRange?.[1].format('YYYY-MM-DD') || '',
          tab: activeTab,
          ...(activeTab === 'order' ? orderFilters : {}),
          ...(activeTab === 'aftersale' ? aftersaleFilters : {}),
          ...(activeTab === 'withdraw' ? withdrawFilters : {}),
          ...(['frozen', 'service'].includes(activeTab) ? settlementFilters : {}),
          ...(activeTab === 'deposit' ? depositFilters : {}),
          ...(['providerSettlement', 'providerIncome'].includes(activeTab) ? providerFlowFilters : {}),
        });
        setReportData(data);
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [activeTab, aftersaleFilters, dateRange, dateRangeExplicit, depositFilters, orderFilters, providerFlowFilters, reportRefreshKey, settlementFilters, withdrawFilters]);

  useEffect(() => {
    if (!isConfigurableFinanceTab(activeTab)) {
      return;
    }

    const loadFinanceTabConfigs = async () => {
      const exportReportKey = getExportReportKey(activeTab);
      const columnReportKey = getColumnReportKey(activeTab);
      const defaultExportKeys = getDefaultExportFieldKeys(activeTab);
      const defaultColumnKeys = getDefaultColumnFieldKeys(activeTab);
      const availableFields = getFinanceDetailFieldsByTab(activeTab);
      try {
        const [exportConfig, columnConfig] = await Promise.all([
          getFinanceReportExportConfig(exportReportKey),
          getFinanceReportExportConfig(columnReportKey),
        ]);
        const normalizedExportKeys = normalizeFinanceDetailFieldKeys(exportConfig.fieldKeys, defaultExportKeys, availableFields);
        const normalizedColumnKeys = normalizeFinanceDetailFieldKeys(columnConfig.fieldKeys, defaultColumnKeys, availableFields);
        setExportFieldKeys(
          activeTab === 'providerIncome'
            ? mergeRequiredFinanceFieldKeys(normalizedExportKeys, defaultProviderIncomeFlowFieldKeys, availableFields)
            : activeTab === 'order'
              ? mergeRequiredFinanceFieldKeys(normalizedExportKeys, requiredOrderIncomeExportFieldKeys, availableFields)
              : normalizedExportKeys,
        );
        setExportLimit(exportConfig.defaultLimit > 0 ? exportConfig.defaultLimit : null);
        setColumnFieldKeys(
          activeTab === 'providerIncome'
            ? mergeRequiredFinanceFieldKeys(normalizedColumnKeys, defaultProviderIncomeFlowFieldKeys, availableFields)
            : activeTab === 'order'
              ? mergeRequiredFinanceFieldKeys(normalizedColumnKeys, requiredOrderIncomeColumnFieldKeys, availableFields)
              : normalizedColumnKeys,
        );
        setColumnSizes(columnConfig.columnSizes || {});
      } catch {
        // Keep local defaults when config loading fails.
        setColumnSizes({});
      }
    };

    void loadFinanceTabConfigs();
  }, [activeTab]);

  const handleOrderSearch = (values?: Record<string, any>) => {
    const formValues = values ?? orderSearchForm.getFieldsValue();
    const nextFilters = {
      customerName: formValues.customerName?.trim() || undefined,
      customerPhone: formValues.customerPhone?.trim() || undefined,
      orderNo: formValues.orderNo?.trim() || undefined,
      orderSource: formValues.orderSource || undefined,
      creationModeGroup: formValues.creationModeGroup || undefined,
      couponCode: formValues.couponCode?.trim() || undefined,
      meituanReservationNo: formValues.meituanReservationNo?.trim() || undefined,
      meituanOrderNo: formValues.meituanOrderNo?.trim() || undefined,
      fulfillmentStartDate: formValues.fulfillmentDateRange?.[0]?.format('YYYY-MM-DD') || undefined,
      fulfillmentEndDate: formValues.fulfillmentDateRange?.[1]?.format('YYYY-MM-DD') || undefined,
      paymentStartDate: formValues.paymentDateRange?.[0]?.format('YYYY-MM-DD') || undefined,
      paymentEndDate: formValues.paymentDateRange?.[1]?.format('YYYY-MM-DD') || undefined,
      verifiedStartDate: formValues.verifiedDateRange?.[0]?.format('YYYY-MM-DD') || undefined,
      verifiedEndDate: formValues.verifiedDateRange?.[1]?.format('YYYY-MM-DD') || undefined,
      verifiedStatus: formValues.verifiedStatus || undefined,
    };
    setOrderFilters(nextFilters);
  };

  const handleOrderReset = () => {
    orderSearchForm.resetFields();
    setDateRange(null);
    setDateRangeExplicit(false);
    setOrderFilters({});
  };

  const handleAftersaleSearch = (values?: Record<string, any>) => {
    const formValues = values ?? aftersaleSearchForm.getFieldsValue();
    const nextFilters = {
      customerName: formValues.customerName?.trim() || undefined,
      customerPhone: formValues.customerPhone?.trim() || undefined,
      orderNo: formValues.orderNo?.trim() || undefined,
    };
    setAftersaleFilters(nextFilters);
  };

  const handleAftersaleReset = () => {
    aftersaleSearchForm.resetFields();
    const nextFilters = {};
    setAftersaleFilters(nextFilters);
  };

  const handleWithdrawSearch = (values?: Record<string, any>) => {
    const formValues = values ?? withdrawSearchForm.getFieldsValue();
    const nextFilters = {
      customerName: formValues.customerName?.trim() || undefined,
      customerPhone: formValues.customerPhone?.trim() || undefined,
    };
    setWithdrawFilters(nextFilters);
  };

  const handleWithdrawReset = () => {
    withdrawSearchForm.resetFields();
    const nextFilters = {};
    setWithdrawFilters(nextFilters);
  };

  const handleSettlementSearch = (values?: Record<string, any>) => {
    const formValues = values ?? settlementSearchForm.getFieldsValue();
    const nextFilters = {
      orderNo: formValues.orderNo?.trim() || undefined,
      title: formValues.title?.trim() || undefined,
      providerName: formValues.providerName?.trim() || undefined,
      providerPhone: formValues.providerPhone?.trim() || undefined,
      customerName: formValues.customerName?.trim() || undefined,
      customerPhone: formValues.customerPhone?.trim() || undefined,
    };
    setSettlementFilters(nextFilters);
  };

  const handleSettlementReset = () => {
    settlementSearchForm.resetFields();
    const nextFilters = {};
    setSettlementFilters(nextFilters);
  };

  const handleDepositSearch = (values?: Record<string, any>) => {
    const formValues = values ?? depositSearchForm.getFieldsValue();
    const nextFilters = {
      customerName: formValues.customerName?.trim() || undefined,
      customerPhone: formValues.customerPhone?.trim() || undefined,
      status: formValues.status || undefined,
    };
    setDepositFilters(nextFilters);
  };

  const handleDepositReset = () => {
    depositSearchForm.resetFields();
    const nextFilters = {};
    setDepositFilters(nextFilters);
  };

  const handleProviderFlowSearch = (values?: Record<string, any>) => {
    const formValues = values ?? settlementSearchForm.getFieldsValue();
    const nextFilters = {
      orderNo: formValues.orderNo?.trim() || undefined,
      providerName: formValues.providerName?.trim() || undefined,
      providerPhone: formValues.providerPhone?.trim() || undefined,
      status: formValues.status || undefined,
    };
    setProviderFlowFilters(nextFilters);
  };

  const handleProviderFlowReset = () => {
    settlementSearchForm.resetFields();
    setProviderFlowFilters({});
  };

  const handleActiveSearch = () => {
    if (isProviderFlowTab) {
      handleProviderFlowSearch();
      return;
    }
    if (isSettlementFlowTab) {
      handleSettlementSearch();
      return;
    }
    if (activeTab === 'deposit') {
      handleDepositSearch();
      return;
    }
    if (activeTab === 'withdraw') {
      handleWithdrawSearch();
      return;
    }
    if (isAftersaleRefundTab) {
      handleAftersaleSearch();
      return;
    }
    handleOrderSearch();
  };

  const handleActiveReset = () => {
    if (isProviderFlowTab) {
      handleProviderFlowReset();
      return;
    }
    if (isSettlementFlowTab) {
      handleSettlementReset();
      return;
    }
    if (activeTab === 'deposit') {
      handleDepositReset();
      return;
    }
    if (activeTab === 'withdraw') {
      handleWithdrawReset();
      return;
    }
    if (isAftersaleRefundTab) {
      handleAftersaleReset();
      return;
    }
    handleOrderReset();
  };

  const moveExportField = (key: ExportFieldKey, direction: 'up' | 'down') => {
    setExportFieldKeys(prev => {
      const currentIndex = prev.indexOf(key);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (currentIndex < 0 || targetIndex < 0 || targetIndex >= prev.length) {
        return prev;
      }
      const next = [...prev];
      const [item] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, item);
      return next;
    });
  };

  const moveExportFieldBefore = (dragKey: ExportFieldKey, targetKey: ExportFieldKey) => {
    setExportFieldKeys(prev => {
      const currentIndex = prev.indexOf(dragKey);
      const targetIndex = prev.indexOf(targetKey);
      if (currentIndex < 0 || targetIndex < 0 || currentIndex === targetIndex) {
        return prev;
      }
      const next = [...prev];
      const [item] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, item);
      return next;
    });
  };

  const handleExportDragStart = (event: React.DragEvent<HTMLElement>, key: ExportFieldKey) => {
    draggingExportKeyRef.current = key;
    setDraggingExportKey(key);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', key);
  };

  const handleExportDragOver = (event: React.DragEvent<HTMLElement>, targetKey: ExportFieldKey) => {
    const dragKey = draggingExportKeyRef.current;
    if (!dragKey || dragKey === targetKey) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    moveExportFieldBefore(dragKey, targetKey);
  };

  const handleExportDragEnd = () => {
    draggingExportKeyRef.current = null;
    setDraggingExportKey(null);
  };

  const handleExportFieldCheck = (key: ExportFieldKey, checked: boolean) => {
    setExportFieldKeys(prev => {
      if (checked) {
        if (prev.includes(key)) {
          return prev;
        }
        return [...prev, key];
      }
      return prev.filter(item => item !== key);
    });
  };

  const moveColumnField = (key: ExportFieldKey, direction: 'up' | 'down') => {
    setColumnFieldKeys(prev => {
      const currentIndex = prev.indexOf(key);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (currentIndex < 0 || targetIndex < 0 || targetIndex >= prev.length) {
        return prev;
      }
      const next = [...prev];
      const [item] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, item);
      return next;
    });
  };

  const moveColumnFieldBefore = (dragKey: ExportFieldKey, targetKey: ExportFieldKey) => {
    setColumnFieldKeys(prev => {
      const currentIndex = prev.indexOf(dragKey);
      const targetIndex = prev.indexOf(targetKey);
      if (currentIndex < 0 || targetIndex < 0 || currentIndex === targetIndex) {
        return prev;
      }
      const next = [...prev];
      const [item] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, item);
      return next;
    });
  };

  const handleColumnDragStart = (event: React.DragEvent<HTMLElement>, key: ExportFieldKey) => {
    draggingColumnKeyRef.current = key;
    setDraggingColumnKey(key);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', key);
  };

  const handleColumnDragOver = (event: React.DragEvent<HTMLElement>, targetKey: ExportFieldKey) => {
    const dragKey = draggingColumnKeyRef.current;
    if (!dragKey || dragKey === targetKey) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    moveColumnFieldBefore(dragKey, targetKey);
  };

  const handleColumnDragEnd = () => {
    draggingColumnKeyRef.current = null;
    setDraggingColumnKey(null);
  };

  const handleColumnFieldCheck = (key: ExportFieldKey, checked: boolean) => {
    setColumnFieldKeys(prev => {
      if (checked) {
        if (prev.includes(key)) {
          return prev;
        }
        return [...prev, key];
      }
      return prev.filter(item => item !== key);
    });
  };

  const filteredRecords = useMemo(() => {
    const records = reportData?.records || [];
    if (activeTab === 'overview') {
      return records;
    }
    return records.filter(item => item.type === activeTab);
  }, [activeTab, reportData?.records]);
  const recordsTotal = reportData?.recordsTotal ?? filteredRecords.length;
  const recordsTruncated = Boolean(reportData?.recordsTruncated);

  const meituanCouponReceipts = useMemo<MeituanOrderQueryInfoReceipt[]>(() => {
    const result = meituanCouponResult;
    const receipts = result && Array.isArray(result.receipts) ? result.receipts : [];
    if (receipts.length > 0) {
      return receipts;
    }
    const receiptList = result && Array.isArray(result.receiptList) ? result.receiptList : [];
    if (receiptList.length > 0) {
      return receiptList.map(item => ({
        ...item,
        serialNumber: item.serialNumber || item.receiptCode,
        receiptStatus: item.receiptStatus ?? item.status,
      }));
    }
    return [];
  }, [meituanCouponResult]);

  const meituanCouponExtraPayments = useMemo<MeituanOrderQueryInfoExtraPayment[]>(() => {
    const result = meituanCouponResult;
    const extraPayments = result && Array.isArray(result.extraPayments) ? result.extraPayments : [];
    return extraPayments;
  }, [meituanCouponResult]);

  const meituanCouponData = meituanCouponResult;
  const meituanCouponOrder = meituanCouponData?.order || {};
  const orderPreAmountList = meituanCouponData?.orderPreAmountList;
  const meituanCouponOrderPreAmountList = Array.isArray(orderPreAmountList) ? orderPreAmountList : [];
  const refundApplicationList = meituanCouponData?.refundApplicationList;
  const meituanCouponRefundApplicationList = Array.isArray(refundApplicationList) ? refundApplicationList : [];

  const availableFinanceDetailFields = useMemo(() => getFinanceDetailFieldsByTab(activeTab), [activeTab]);

  const selectedExportFields = useMemo(
    () =>
      exportFieldKeys
        .map(key => availableFinanceDetailFields.find(item => item.key === key))
        .filter((item): item is ExportFieldOption => Boolean(item)),
    [availableFinanceDetailFields, exportFieldKeys],
  );
  const unselectedExportFields = useMemo(
    () => availableFinanceDetailFields.filter(field => !exportFieldKeys.includes(field.key)),
    [availableFinanceDetailFields, exportFieldKeys],
  );
  const selectedColumnFields = useMemo(
    () =>
      columnFieldKeys
        .map(key => availableFinanceDetailFields.find(item => item.key === key))
        .filter((item): item is ExportFieldOption => Boolean(item)),
    [availableFinanceDetailFields, columnFieldKeys],
  );
  const unselectedColumnFields = useMemo(
    () => availableFinanceDetailFields.filter(field => !columnFieldKeys.includes(field.key)),
    [availableFinanceDetailFields, columnFieldKeys],
  );
  const activeReportLabel =
    activeTab === 'aftersale'
      ? '售后退款'
      : activeTab === 'withdraw'
        ? '服务人员提现'
        : activeTab === 'frozen'
          ? '服务流水已结算'
          : activeTab === 'service'
            ? '服务流水未结算'
            : activeTab === 'deposit'
              ? '保证金'
              : activeTab === 'providerSettlement'
                ? '代理商结算流水'
                : activeTab === 'providerIncome'
                  ? '代理商收入流水'
              : '订单收入';
  const activeDateLabel =
    activeTab === 'aftersale'
      ? '退款时间'
      : activeTab === 'withdraw'
        ? '到账时间'
        : activeTab === 'frozen' || activeTab === 'service'
          ? '完成日期'
          : activeTab === 'deposit'
            ? '日期'
            : activeTab === 'providerSettlement' || activeTab === 'providerIncome'
              ? '发生日期'
            : '支付时间';
  const activeExportReportKey = isConfigurableDetailTab ? getExportReportKey(activeTab) : orderIncomeReportKey;
  const activeColumnReportKey = isConfigurableDetailTab ? getColumnReportKey(activeTab) : orderIncomeColumnReportKey;
  const activeDefaultExportFieldKeys = isConfigurableDetailTab
    ? getDefaultExportFieldKeys(activeTab)
    : defaultOrderIncomeExportFieldKeys;
  const activeDefaultColumnFieldKeys = isConfigurableDetailTab
    ? getDefaultColumnFieldKeys(activeTab)
    : defaultOrderIncomeColumnFieldKeys;

  const handleExportFinanceRecords = () => {
    if (exportModalMode === 'config') {
      if (selectedExportFields.length === 0) {
        message.warning('请至少选择一个导出字段');
        return;
      }
      setExportConfigLoading(true);
      saveFinanceReportExportConfig({
        reportKey: activeExportReportKey,
        fieldKeys: selectedExportFields.map(field => field.key),
        defaultLimit: exportLimit && exportLimit > 0 ? exportLimit : 0,
        columnSizes: {},
      })
        .then(config => {
          setExportFieldKeys(
            activeTab === 'order'
              ? mergeRequiredFinanceFieldKeys(
                  normalizeFinanceDetailFieldKeys(config.fieldKeys, activeDefaultExportFieldKeys, availableFinanceDetailFields),
                  requiredOrderIncomeExportFieldKeys,
                  availableFinanceDetailFields,
                )
              : normalizeFinanceDetailFieldKeys(config.fieldKeys, activeDefaultExportFieldKeys, availableFinanceDetailFields),
          );
          setExportLimit(config.defaultLimit > 0 ? config.defaultLimit : null);
          setExportModalOpen(false);
          message.success('导出配置已保存');
        })
        .finally(() => setExportConfigLoading(false));
      return;
    }

    if (selectedExportFields.length === 0) {
      message.warning('请至少选择一个导出字段');
      return;
    }
    if (filteredRecords.length === 0) {
      message.warning(`当前没有可导出的${activeReportLabel}数据`);
      return;
    }
    if (recordsTruncated) {
      message.warning(`当前筛选共 ${recordsTotal} 条，仅展示前 ${filteredRecords.length} 条；请缩小筛选范围后再导出完整数据`);
      return;
    }

    const exportRecords = exportLimit && exportLimit > 0 ? filteredRecords.slice(0, exportLimit) : filteredRecords;
    const rows = exportRecords.map(record => {
      const row: Record<string, string | number> = {};
      selectedExportFields.forEach(field => {
        row[getFinanceDetailFieldLabel(field, activeTab)] = field.getValue(record);
      });
      return row;
    });
    import('xlsx').then(XLSX => {
      const worksheet = XLSX.utils.json_to_sheet(rows, {
      header: selectedExportFields.map(field => getFinanceDetailFieldLabel(field, activeTab)),
    });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, activeReportLabel);
      const exportStart = dateRange?.[0]?.format('YYYYMMDD') || '全部';
      const exportEnd = dateRange?.[1]?.format('YYYYMMDD') || '全部';
      XLSX.writeFile(workbook, `${activeReportLabel}_${exportStart}_${exportEnd}.xlsx`);
      setExportModalOpen(false);
    });
  };

  const handleSaveColumnConfig = () => {
    if (selectedColumnFields.length === 0) {
      message.warning('请至少选择一个列表字段');
      return;
    }
    const selectedColumnKeys = new Set(selectedColumnFields.map(field => field.key));
    const normalizedColumnSizes = Object.fromEntries(
      Object.entries(columnSizes).filter(([key, size]) => {
        return selectedColumnKeys.has(key as ExportFieldKey) && Boolean(size.width || size.height);
      }),
    );
    setColumnConfigLoading(true);
    saveFinanceReportExportConfig({
      reportKey: activeColumnReportKey,
      fieldKeys: selectedColumnFields.map(field => field.key),
      defaultLimit: 0,
      columnSizes: normalizedColumnSizes,
    })
      .then(config => {
        setColumnFieldKeys(
          activeTab === 'order'
            ? mergeRequiredFinanceFieldKeys(
                normalizeFinanceDetailFieldKeys(config.fieldKeys, activeDefaultColumnFieldKeys, availableFinanceDetailFields),
                requiredOrderIncomeColumnFieldKeys,
                availableFinanceDetailFields,
              )
            : normalizeFinanceDetailFieldKeys(config.fieldKeys, activeDefaultColumnFieldKeys, availableFinanceDetailFields),
        );
        setColumnSizes(config.columnSizes || {});
        setColumnConfigModalOpen(false);
        message.success('列配置已保存');
      })
      .finally(() => setColumnConfigLoading(false));
  };

  const orderIncomeChartData = useMemo(() => {
    const dayMap = new Map<string, { amount: number; count: number }>();
    const channelMap = new Map<string, number>();
    const statusMap = new Map<string, number>();
    filteredRecords.forEach(record => {
      const date = getOrderChartDate(record);
      const dayValue = dayMap.get(date) || { amount: 0, count: 0 };
      dayValue.amount += Number(record.amount || 0);
      dayValue.count += 1;
      dayMap.set(date, dayValue);

      const channel = record.payChannel || '未知渠道';
      channelMap.set(channel, (channelMap.get(channel) || 0) + Number(record.amount || 0));

      const status = record.status || '未知状态';
      statusMap.set(status, (statusMap.get(status) || 0) + 1);
    });

    const days = Array.from(dayMap.keys()).sort();
    return {
      days,
      amounts: days.map(day => Number(dayMap.get(day)?.amount.toFixed(2) || 0)),
      counts: days.map(day => dayMap.get(day)?.count || 0),
      channelPie: Array.from(channelMap.entries()).map(([name, value]) => ({
        name,
        value: Number(value.toFixed(2)),
      })),
      statusPie: Array.from(statusMap.entries()).map(([name, value]) => ({ name, value })),
    };
  }, [filteredRecords]);

  const orderIncomeLineOption = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        valueFormatter: (value: number) => moneyFormatter(value),
      },
      grid: { left: 56, right: 24, top: 36, bottom: 44 },
      xAxis: {
        type: 'category',
        data: orderIncomeChartData.days,
        axisLine: { lineStyle: { color: '#d6dfeb' } },
      },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (value: number) => `￥${value}` },
        splitLine: { lineStyle: { color: '#eef3f9' } },
      },
      series: [
        {
          name: activeReportLabel,
          type: 'line',
          smooth: true,
          symbolSize: 7,
          data: orderIncomeChartData.amounts,
          areaStyle: { opacity: 0.12 },
          lineStyle: { width: 3 },
        },
      ],
    }),
    [activeReportLabel, orderIncomeChartData],
  );

  const orderIncomeBarOption = useMemo(
    () => ({
      tooltip: { trigger: 'axis' },
      legend: { top: 0, data: [`${activeReportLabel}金额`, '数量'] },
      grid: { left: 56, right: 56, top: 48, bottom: 44 },
      xAxis: {
        type: 'category',
        data: orderIncomeChartData.days,
        axisLine: { lineStyle: { color: '#d6dfeb' } },
      },
      yAxis: [
        {
          type: 'value',
          name: '金额',
          axisLabel: { formatter: (value: number) => `￥${value}` },
          splitLine: { lineStyle: { color: '#eef3f9' } },
        },
        {
          type: 'value',
          name: '订单数',
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: `${activeReportLabel}金额`,
          type: 'bar',
          barMaxWidth: 30,
          data: orderIncomeChartData.amounts,
        },
        {
          name: '数量',
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          data: orderIncomeChartData.counts,
        },
      ],
    }),
    [activeReportLabel, orderIncomeChartData],
  );

  const orderIncomePieOption = useMemo(
    () => ({
      tooltip: { trigger: 'item' },
      legend: { bottom: 0 },
      series: [
        {
          name: activeTab === 'aftersale' ? '退款渠道金额' : '支付渠道金额',
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['28%', '44%'],
          data: orderIncomeChartData.channelPie,
        },
        {
          name: activeTab === 'aftersale' ? '退款状态数量' : '订单状态数量',
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['72%', '44%'],
          data: orderIncomeChartData.statusPie,
        },
      ],
      graphic: [
        {
          type: 'text',
          left: '23%',
          top: '42%',
          style: { text: activeTab === 'aftersale' ? '退款渠道' : '支付渠道', fill: '#667085', fontSize: 13, fontWeight: 600 },
        },
        {
          type: 'text',
          left: '67%',
          top: '42%',
          style: { text: activeTab === 'aftersale' ? '退款状态' : '订单状态', fill: '#667085', fontSize: 13, fontWeight: 600 },
        },
      ],
    }),
    [activeTab, orderIncomeChartData],
  );

  const trendOption = useMemo(() => {
    const trend = reportData?.trend || [];
    return {
      tooltip: { trigger: 'axis' },
      legend: {
        top: 0,
        data: ['订单收入', '订单支出', '售后', '服务人员提现', '服务流水未结算', '服务流水已结算', '保证金', '代理商结算流水', '代理商收入流水'],
      },
      grid: { left: 48, right: 24, top: 56, bottom: 36 },
      xAxis: {
        type: 'category',
        data: trend.map(item => item.date.slice(5)),
        axisLine: { lineStyle: { color: '#d6dfeb' } },
      },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (value: number) => `￥${value}` },
        splitLine: { lineStyle: { color: '#eef3f9' } },
      },
      series: [
        {
          name: '订单收入',
          type: 'line',
          smooth: true,
          data: trend.map(item => item.orderIncome),
          areaStyle: { opacity: 0.08 },
        },
        {
          name: '订单支出',
          type: 'line',
          smooth: true,
          data: trend.map(item => item.orderExpense),
        },
        {
          name: '售后',
          type: 'bar',
          data: trend.map(item => item.aftersaleAmount),
        },
        {
          name: '服务人员提现',
          type: 'bar',
          data: trend.map(item => item.withdrawAmount),
        },
        {
          name: '服务流水未结算',
          type: 'line',
          smooth: true,
          data: trend.map(item => item.frozenAmount),
        },
        {
          name: '服务流水已结算',
          type: 'line',
          smooth: true,
          data: trend.map(item => item.serviceFlowAmount),
        },
        {
          name: '保证金',
          type: 'bar',
          data: trend.map(item => item.depositAmount),
        },
        {
          name: '代理商结算流水',
          type: 'bar',
          data: trend.map(item => item.providerSettlementAmount || 0),
        },
        {
          name: '代理商收入流水',
          type: 'line',
          smooth: true,
          data: trend.map(item => item.providerIncomeAmount || 0),
        },
      ],
    };
  }, [reportData?.trend]);

  const pieOption = useMemo(() => {
    const summary = reportData?.summary;
    return {
      tooltip: { trigger: 'item' },
      legend: { bottom: 0 },
      series: [
        {
          name: '财务分类',
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['50%', '44%'],
          data: [
            { name: '订单收入', value: summary?.orderIncome || 0 },
            { name: '订单支出', value: summary?.orderExpense || 0 },
            { name: '售后', value: summary?.aftersaleAmount || 0 },
            { name: '服务人员提现', value: summary?.withdrawAmount || 0 },
            { name: '服务流水未结算', value: summary?.frozenAmount || 0 },
            { name: '服务流水已结算', value: summary?.serviceFlowAmount || 0 },
            { name: '保证金', value: summary?.depositAmount || 0 },
            { name: '代理商结算流水', value: summary?.providerSettlementAmount || 0 },
            { name: '代理商收入流水', value: summary?.providerIncomeAmount || 0 },
          ],
        },
      ],
    };
  }, [reportData?.summary]);

  const hideRelatedOrder = ['withdraw', 'deposit'].includes(activeTab);
  const hideSubjectPhone = ['aftersale'].includes(activeTab);
  const hideRemark = ['overview', 'order', 'aftersale', 'frozen', 'service'].includes(activeTab);
  const hideFlowNo = activeTab === 'order';
  const hideType = activeTab === 'order';
  const hideTitle = activeTab === 'order';
  const isOrderIncomeTab = activeTab === 'order';
  const isOrderDetailMeituan =
    String(orderDetailRecord?.orderSource || orderDetailResult?.source || '').trim().toLowerCase() === '美团' ||
    String(orderDetailRecord?.meituanOrderNo || orderDetailResult?.externalOrderId || '').trim() !== '' ||
    String(orderDetailRecord?.meituanReservationNo || orderDetailResult?.externalReservationNo || '').trim() !== '';
  const isAftersaleRefundTab = activeTab === 'aftersale';
  const isSettlementFlowTab = activeTab === 'frozen' || activeTab === 'service';
  const isProviderFlowTab = activeTab === 'providerSettlement' || activeTab === 'providerIncome';
  const getFinanceServiceOrderId = (record: FinanceFlowRecord) => {
    const explicitID = String(record.serviceOrderId || '').trim();
    if (explicitID) {
      return explicitID;
    }
    return String(record.id || '').replace(/^(?:meituan_operational_|manual_wechat_|payment_)/, '').trim();
  };
  const canViewMeituanCoupons = (record: FinanceFlowRecord) =>
    Boolean(getFinanceServiceOrderId(record)) &&
    (String(record.orderSource || '').trim() === '美团' ||
      String(record.meituanOrderNo || '').trim() ||
      String(record.meituanReservationNo || '').trim());

  const handleOpenMeituanCoupons = async (record: FinanceFlowRecord) => {
    const serviceOrderId = getFinanceServiceOrderId(record);
    if (!serviceOrderId) {
      message.warning('缺少平台订单ID，暂不能查询券码');
      return;
    }
    setMeituanCouponRecord(record);
    setMeituanCouponModalOpen(true);
    setMeituanCouponLoading(true);
    try {
      const response = await serviceBookingOrderService.queryMeituanOrderInfo(serviceOrderId);
      setMeituanCouponResult(response);
      setReportRefreshKey(prev => prev + 1);
    } catch {
      setMeituanCouponResult(null);
      message.error('美团券码查询失败');
    } finally {
      setMeituanCouponLoading(false);
    }
  };

  const handleOpenMeituanPaymentShares = async (record: FinanceFlowRecord) => {
    const serviceOrderId = getFinanceServiceOrderId(record);
    if (!serviceOrderId) {
      message.warning('缺少平台订单ID，暂不能查询分摊明细');
      return;
    }
    setMeituanPaymentSharesRecord(record);
    setMeituanPaymentSharesModalOpen(true);
    setMeituanPaymentSharesLoading(true);
    try {
      const response = await serviceBookingOrderService.queryMeituanPaymentShares(serviceOrderId);
      setMeituanPaymentSharesResult(response);
    } catch {
      setMeituanPaymentSharesResult(null);
      message.error('美团分摊明细查询失败');
    } finally {
      setMeituanPaymentSharesLoading(false);
    }
  };

  const handleOpenOrderDetail = async (record: FinanceFlowRecord) => {
    const serviceOrderId = getFinanceServiceOrderId(record);
    if (!serviceOrderId) {
      message.warning('缺少平台订单ID，暂不能查看订单详情');
      return;
    }
    setOrderDetailRecord(record);
    setOrderDetailModalOpen(true);
    setOrderDetailLoading(true);
    try {
      const response = await serviceBookingOrderService.get(serviceOrderId);
      setOrderDetailResult(response);
    } catch {
      setOrderDetailResult(null);
      message.error('加载订单详情失败');
    } finally {
      setOrderDetailLoading(false);
    }
  };

  const currentSuborderCouponCodes = useMemo(() => {
    const codes = new Set<string>();
    const verifiedText = String(meituanPaymentSharesRecord?.meituanVerifiedCode || '');
    for (const code of verifiedText.split(/[,，、;；/\s]+/)) {
      const normalized = code.trim();
      if (normalized) codes.add(normalized);
    }
    if (codes.size === 0) {
      for (const item of meituanPaymentSharesRecord?.couponInfo || []) {
        if (item.verified || Number(item.verifyStatus) === 2) {
          const code = String(item.couponCode || '').trim();
          if (code) codes.add(code);
        }
      }
    }
    return Array.from(codes);
  }, [meituanPaymentSharesRecord]);

  const meituanPaymentShareGroups = useMemo(() => {
    const rows = meituanPaymentSharesResult?.result || [];
    const relatedCodes = new Set(currentSuborderCouponCodes);
    if (relatedCodes.size === 0) {
      return { primary: rows, rest: [] as MeituanPaymentShareItem[] };
    }
    const primary: MeituanPaymentShareItem[] = [];
    const rest: MeituanPaymentShareItem[] = [];
    for (const row of rows) {
      if (relatedCodes.has(String(row.receiptCode || '').trim())) primary.push(row);
      else rest.push(row);
    }
    return { primary, rest };
  }, [currentSuborderCouponCodes, meituanPaymentSharesResult]);

  const frozenDateColumns: ColumnsType<FinanceFlowRecord> =
    activeTab === 'frozen'
      ? [
          {
            title: '服务日期',
            dataIndex: 'serviceDate',
            width: 140,
            render: (serviceDate: string) => serviceDate || '--',
          },
          {
            title: '结算日期',
            dataIndex: 'settlementDate',
            width: 170,
            render: (settlementDate: string) => settlementDate || '--',
          },
        ]
      : [];
  const subjectPhoneColumns: ColumnsType<FinanceFlowRecord> = hideSubjectPhone
    ? []
    : [
        {
          title: activeTab === 'withdraw' ? '电话' : '对象电话',
          dataIndex: 'subjectPhone',
          width: 150,
          render: (subjectPhone: string) => subjectPhone || '--',
        },
      ];
  const relatedOrderColumns: ColumnsType<FinanceFlowRecord> = hideRelatedOrder
    ? []
    : [
        {
          title: isOrderIncomeTab ? '订单号' : '关联订单',
          dataIndex: 'relatedOrderNo',
          width: 180,
          render: (relatedOrderNo: string, record: FinanceFlowRecord) => {
            if (!relatedOrderNo) {
              return '--';
            }
            return isOrderIncomeTab ? (
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => void handleOpenOrderDetail(record)}
                style={{ paddingInline: 0 }}
              >
                {relatedOrderNo}
              </Button>
            ) : relatedOrderNo;
          },
        },
      ];
  const financeDetailColumns: ColumnsType<FinanceFlowRecord> = selectedColumnFields.map(field => ({
    title: getFinanceDetailFieldLabel(field, activeTab),
    dataIndex: field.key,
    width: columnSizes[field.key]?.width || financeDetailColumnWidthMap[field.key] || 150,
    render: (_value: unknown, record: FinanceFlowRecord) => {
      const renderWithHeight = (content: React.ReactNode) => {
        const height = columnSizes[field.key]?.height;
        return (
          <div style={height && height > 0 ? { minHeight: height, display: 'flex', alignItems: 'center' } : undefined}>
            {content}
          </div>
        );
      };
      if (field.key === 'amount') {
        const prefix = record.direction === 'expense' ? '-' : '+';
        return renderWithHeight(
          <Text strong type={record.direction === 'expense' ? 'danger' : 'success'}>
            {prefix}
            {moneyFormatter(record.amount)}
          </Text>,
        );
      }
      if (field.key === 'relatedOrderNo') {
        const relatedOrderNo = String(record.relatedOrderNo || '').trim();
        return renderWithHeight(
          isOrderIncomeTab && relatedOrderNo ? (
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => void handleOpenOrderDetail(record)}
              style={{
                paddingInline: 0,
                height: 'auto',
                maxWidth: '100%',
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                textAlign: 'left',
                lineHeight: 1.5,
                userSelect: 'text',
              }}
            >
              {relatedOrderNo}
            </Button>
          ) : relatedOrderNo || '--',
        );
      }
      if (['unitPrice', 'totalAmount', 'orderAmount', 'estimatedAmount', 'meituanVerifiedAmount', 'meituanVerifiedGrossAmount', 'meituanMerchantFundedAmount', 'meituanMerchantMarketingAmount'].includes(field.key)) {
        return renderWithHeight(moneyFormatter(Number(field.getValue(record) || 0)));
      }
      if (field.key === 'settlementRatio') {
        return renderWithHeight(`${Number(field.getValue(record) || 0).toFixed(2)}%`);
      }
      if (field.key === 'couponInfo') {
        const couponInfo = normalizeCouponInfoList(record.couponInfo);
        const isMeituanOrder =
          String(record.orderSource || '').trim() === '美团' ||
          Boolean(String(record.meituanOrderNo || '').trim()) ||
          Boolean(String(record.meituanReservationNo || '').trim());
        if (!isMeituanOrder) {
          return renderWithHeight('--');
        }
        if (couponInfo.length === 0) {
          return renderWithHeight('--');
        }
        return renderWithHeight(
          <Space orientation="vertical" size={4} style={{ width: '100%' }}>
            {couponInfo.map((item, index) => {
              const status = Number(item.verifyStatus || 0);
              const statusConfig = meituanReceiptStatusMap[status] || {
                color: item.verified ? 'success' : 'default',
                text: formatCouponVerifyStatusText(item),
              };
              return (
                <div key={`${item.couponCode || 'coupon'}-${index}`} style={{ lineHeight: 1.6 }}>
                  <div style={{ wordBreak: 'break-all' }}>券码：{item.couponCode || '-'}</div>
                  <Space size={6} wrap>
                    <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
                    <Text type="secondary">金额：{moneyFormatter(Number(item.amount || 0))}</Text>
                  </Space>
                </div>
              );
            })}
          </Space>,
        );
      }
      if (field.key === 'meituanAddPriceDetail') {
        const isMeituanOrder =
          String(record.orderSource || '').trim() === '美团' ||
          Boolean(String(record.meituanOrderNo || '').trim()) ||
          Boolean(String(record.meituanReservationNo || '').trim());
        if (!isMeituanOrder) {
          return renderWithHeight('--');
        }
        const detail = String(record.meituanAddPriceDetail || '').trim();
        if (!detail) {
          return renderWithHeight('--');
        }
        return renderWithHeight(
          <div style={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
            {detail}
          </div>,
        );
      }
      return renderWithHeight(renderEmpty(field.getValue(record)));
    },
  }));
  const orderActionColumns: ColumnsType<FinanceFlowRecord> =
    isOrderIncomeTab
      ? [
          {
            title: '操作',
            key: 'actions',
            width: 95,
            fixed: 'right',
            render: (_value, record) =>
              canViewMeituanCoupons(record) ? (
                <Space orientation="vertical" size={0}>
                  <Button type="link" size="small" style={{ paddingInline: 0, height: 22 }} onClick={() => void handleOpenMeituanCoupons(record)}>
                    券码查看
                  </Button>
                  <Button type="link" size="small" style={{ paddingInline: 0, height: 22 }} onClick={() => void handleOpenMeituanPaymentShares(record)}>
                    分摊明细
                  </Button>
                </Space>
              ) : (
                '--'
              ),
          },
        ]
      : [];
  const baseColumns: ColumnsType<FinanceFlowRecord> = [
    ...(!hideFlowNo
      ? [
          {
            title: '流水号',
            dataIndex: 'flowNo',
            width: 190,
          },
        ]
      : []),
    ...(!hideType
      ? [
          {
            title: '类型',
            dataIndex: 'type',
            width: 130,
            render: (type: FinanceFlowRecord['type']) => <Tag color="blue">{financeFlowTypeText(type)}</Tag>,
          },
        ]
      : []),
    ...(!hideTitle
      ? [
          {
            title: '标题',
            dataIndex: 'title',
            width: 160,
          },
        ]
      : []),
    {
      title: '金额',
      dataIndex: 'amount',
      width: 140,
      render: (amount: number, record) => (
        <Text
          type={record.direction === 'income' ? 'success' : record.direction === 'freeze' ? 'warning' : 'danger'}
          strong
        >
          {record.direction === 'income' ? '+' : record.direction === 'freeze' ? '' : '-'}
          {moneyFormatter(amount)}
        </Text>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
    },
    {
      title: '姓名',
      dataIndex: 'subjectName',
      width: 180,
      render: (subjectName: string) => subjectName || '--',
    },
    ...subjectPhoneColumns,
    ...relatedOrderColumns,
    ...frozenDateColumns,
    {
      title: '发生时间',
      dataIndex: 'occurredAt',
      width: 170,
    },
    ...(!hideRemark
      ? [
          {
            title: '备注',
            dataIndex: 'remark',
          },
        ]
      : []),
  ];
  const columns = (isConfigurableDetailTab ? financeDetailColumns : baseColumns).concat(orderActionColumns);
  const tableScrollX = isConfigurableDetailTab
    ? selectedColumnFields.reduce((sum, field) => sum + (columnSizes[field.key]?.width || financeDetailColumnWidthMap[field.key] || 150), 0) +
      orderActionColumns.reduce((sum, column) => sum + Number(column.width || 0), 0)
    : 1410 + orderActionColumns.reduce((sum, column) => sum + Number(column.width || 0), 0);

  const summary = reportData?.summary;
  const summaryCards = (() => {
    if (isAftersaleRefundTab) {
      return [
        {
          title: '售后退款金额',
          value: summary?.aftersaleAmount || 0,
          precision: 2,
          prefix: <RollbackOutlined />,
        },
        {
          title: '成功退款笔数',
          value: summary?.transactionCount || 0,
          prefix: <AccountBookOutlined />,
        },
      ];
    }

    if (activeTab === 'withdraw') {
      return [
        {
          title: '服务人员提现金额',
          value: summary?.withdrawAmount || 0,
          precision: 2,
          prefix: <WalletOutlined />,
        },
        {
          title: '已到账笔数',
          value: filteredRecords.length,
          prefix: <AccountBookOutlined />,
        },
      ];
    }

    if (activeTab === 'frozen') {
      return [
        {
          title: '服务流水已结算金额',
          value: summary?.serviceFlowAmount || 0,
          precision: 2,
          prefix: <BankOutlined />,
        },
        {
          title: '已结算笔数',
          value: filteredRecords.length,
          prefix: <AccountBookOutlined />,
        },
      ];
    }

    if (activeTab === 'service') {
      return [
        {
          title: '服务流水未结算金额',
          value: summary?.frozenAmount || 0,
          precision: 2,
          prefix: <LockOutlined />,
        },
        {
          title: '待结算笔数',
          value: filteredRecords.length,
          prefix: <AccountBookOutlined />,
        },
      ];
    }

    if (activeTab === 'deposit') {
      return [
        {
          title: '保证金金额',
          value: summary?.depositAmount || 0,
          precision: 2,
          prefix: <SafetyCertificateOutlined />,
        },
        {
          title: '保证金流水笔数',
          value: filteredRecords.length,
          prefix: <AccountBookOutlined />,
        },
      ];
    }

    if (activeTab === 'providerSettlement') {
      return [
        {
          title: '代理商结算金额',
          value: summary?.providerSettlementAmount || 0,
          precision: 2,
          prefix: <WalletOutlined />,
        },
        {
          title: '结算流水笔数',
          value: filteredRecords.length,
          prefix: <AccountBookOutlined />,
        },
      ];
    }

    if (activeTab === 'providerIncome') {
      return [
        {
          title: '代理商收入金额',
          value: summary?.providerIncomeAmount || 0,
          precision: 2,
          prefix: <DollarOutlined />,
        },
        {
          title: '收入流水笔数',
          value: filteredRecords.length,
          prefix: <AccountBookOutlined />,
        },
      ];
    }

    if (isOrderIncomeTab) {
      const meituanMetric = (() => {
        if (orderFilters.verifiedStatus === 'verified') {
          return {
            amountTitle: '美团核销金额',
            amount: summary?.meituanVerifiedIncome || 0,
            couponTitle: '美团已核销码数',
            couponCount: summary?.verifiedCouponCount || 0,
          };
        }
        if (orderFilters.verifiedStatus === 'unverified') {
          return {
            amountTitle: '美团未核销金额',
            amount: summary?.meituanIncome || 0,
            couponTitle: '美团未核销码数',
            couponCount: summary?.meituanUnverifiedCouponCount || 0,
          };
        }
        return {
          amountTitle: '美团收入',
          amount: summary?.meituanIncome || 0,
          couponTitle: '美团已核销码数',
          couponCount: summary?.verifiedCouponCount || 0,
        };
      })();

      return [
        {
          title: '收入合计',
          value: summary?.orderIncome || 0,
          precision: 2,
          prefix: <DollarOutlined />,
        },
        {
          title: '订单数合计',
          value: summary?.transactionCount || 0,
          prefix: <AccountBookOutlined />,
        },
        {
          title: meituanMetric.amountTitle,
          value: meituanMetric.amount,
          precision: 2,
          prefix: <SafetyCertificateOutlined />,
          showMeituanInfo: true,
          meituanInfoKind: 'amount' as const,
        },
        {
          title: meituanMetric.couponTitle,
          value: meituanMetric.couponCount,
          prefix: <QrcodeOutlined />,
          showMeituanInfo: true,
          meituanInfoKind: 'count' as const,
        },
        {
          title: '小程序收款金额',
          value: summary?.miniProgramIncome || 0,
          precision: 2,
          prefix: <DollarOutlined />,
        },
        {
          title: '小程序收款笔数',
          value: summary?.miniProgramPaymentCount || 0,
          prefix: <AccountBookOutlined />,
        },
        {
          title: '微信收款金额',
          value: summary?.wechatIncome || 0,
          precision: 2,
          prefix: <DollarOutlined />,
        },
        {
          title: '微信支付收款笔数',
          value: summary?.wechatPaymentCount || 0,
          prefix: <AccountBookOutlined />,
        },
        {
          title: '对公收款金额',
          value: summary?.corporateIncome || 0,
          precision: 2,
          prefix: <BankOutlined />,
        },
        {
          title: '对公收款笔数',
          value: summary?.corporatePaymentCount || 0,
          prefix: <AccountBookOutlined />,
        },
      ];
    }

    const cards = [
      {
        title: '订单收入',
        value: summary?.orderIncome || 0,
        precision: 2,
        prefix: <DollarOutlined />,
      },
      {
        title: '已支付订单数',
        value: summary?.transactionCount || 0,
        prefix: <AccountBookOutlined />,
      },
      {
        title: '完成履约金额',
        value: summary?.fulfilledOrderAmount || 0,
        precision: 2,
        prefix: <SafetyCertificateOutlined />,
      },
      {
        title: '完成履约笔数',
        value: summary?.fulfilledOrderCount || 0,
        prefix: <AccountBookOutlined />,
      },
      {
        title: '核销金额',
        value: summary?.verifiedCouponAmount || 0,
        precision: 2,
        prefix: <SafetyCertificateOutlined />,
      },
      {
        title: '核销券码数',
        value: summary?.verifiedCouponCount || 0,
        prefix: <AccountBookOutlined />,
      },
    ];

    cards.push(
      {
        title: '订单支出',
        value: summary?.orderExpense || 0,
        precision: 2,
        prefix: <ArrowDownOutlined />,
      },
      {
        title: '净额',
        value: summary?.netAmount || 0,
        precision: 2,
        prefix: <ArrowUpOutlined />,
      },
      {
        title: '售后金额',
        value: summary?.aftersaleAmount || 0,
        precision: 2,
        prefix: <RollbackOutlined />,
      },
      {
        title: '服务人员提现金额',
        value: summary?.withdrawAmount || 0,
        precision: 2,
        prefix: <WalletOutlined />,
      },
      {
        title: '服务流水未结算金额',
        value: summary?.frozenAmount || 0,
        precision: 2,
        prefix: <LockOutlined />,
      },
      {
        title: '服务流水已结算金额',
        value: summary?.serviceFlowAmount || 0,
        precision: 2,
        prefix: <BankOutlined />,
      },
      {
        title: '保证金',
        value: summary?.depositAmount || 0,
        precision: 2,
        prefix: <SafetyCertificateOutlined />,
      },
      {
        title: '代理商结算流水',
        value: summary?.providerSettlementAmount || 0,
        precision: 2,
        prefix: <WalletOutlined />,
      },
      {
        title: '代理商收入流水',
        value: summary?.providerIncomeAmount || 0,
        precision: 2,
        prefix: <DollarOutlined />,
      },
    );

    return cards;
  })();

  return (
    <div>
      <div className="page-header">
        <Title level={2}>财务报表</Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          查看订单收入、售后、服务人员提现、服务流水已结算、服务流水未结算、保证金和代理商流水，默认展示近一个月数据。
        </Paragraph>
      </div>

      <div className="page-content">
        <Card style={{ marginBottom: 16 }}>
          <div style={{ alignItems: 'center', display: 'flex', gap: 16, justifyContent: 'space-between' }}>
            <Space size={16} wrap>
              <RangePicker
                value={dateRange}
                allowClear
                onChange={dates => {
                  if (dates?.[0] && dates?.[1]) {
                    setDateRange([dates[0], dates[1]]);
                    setDateRangeExplicit(true);
                  } else {
                    setDateRange(null);
                    setDateRangeExplicit(false);
                  }
                }}
              />
              <Tag icon={<ReloadOutlined />} color="processing">
                当前为财务报表统计视图
              </Tag>
            </Space>
            <Button type="link" icon={<InfoCircleOutlined />} onClick={() => setStatisticsRuleModalOpen(true)}>
              统计口径说明
            </Button>
          </div>
        </Card>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {summaryCards.map(card => (
            <Col key={card.title} xs={24} sm={12} lg={6} xl={isOrderIncomeTab ? 4 : 6} xxl={isOrderIncomeTab ? 4 : 6}>
              <Card loading={loading} style={{ height: '100%', minHeight: 112 }}>
                <Statistic
                  title={card.showMeituanInfo ? (
                    <Space size={4}>
                      <span>{card.title}</span>
                      <Button
                        type="text"
                        size="small"
                        icon={<InfoCircleOutlined />}
                        aria-label={card.meituanInfoKind === 'count' ? '查看美团核销数量与状态' : '查看美团核销金额构成'}
                        onClick={() => {
                          if (card.meituanInfoKind === 'count') {
                            setMeituanCouponStatsModalOpen(true);
                          } else {
                            setMeituanAmountModalOpen(true);
                          }
                        }}
                      />
                    </Space>
                  ) : card.title}
                  value={card.value}
                  precision={card.precision}
                  prefix={card.prefix}
                />
              </Card>
            </Col>
          ))}
        </Row>

        <Tabs activeKey={activeTab} onChange={key => navigate(`/finance/report/${financeTabPathMap[key as FinanceReportTab] || key}`)} items={tabItems} />

        {activeTab === 'overview' && (
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} xl={16}>
              <Card title="收入支出趋势" loading={loading}>
                <ReactECharts option={trendOption} style={{ height: 360 }} />
              </Card>
            </Col>
            <Col xs={24} xl={8}>
              <Card title="财务分类占比" loading={loading}>
                <ReactECharts option={pieOption} style={{ height: 360 }} />
              </Card>
            </Col>
          </Row>
        )}

        <Card title={activeTab === 'overview' ? '全部服务流水' : `${tabItems.find(item => item.key === activeTab)?.label}明细`}>
          {isConfigurableDetailTab && (
            <Form
              form={
                isProviderFlowTab || isSettlementFlowTab
                  ? settlementSearchForm
                  : activeTab === 'deposit'
                    ? depositSearchForm
                    : activeTab === 'withdraw'
                      ? withdrawSearchForm
                      : isAftersaleRefundTab
                        ? aftersaleSearchForm
                        : orderSearchForm
              }
              layout="inline"
              style={{ marginBottom: 16 }}
              onFinish={values => {
                if (isProviderFlowTab) {
                  handleProviderFlowSearch(values);
                  return;
                }
                if (isSettlementFlowTab) {
                  handleSettlementSearch(values);
                  return;
                }
                if (activeTab === 'deposit') {
                  handleDepositSearch(values);
                  return;
                }
                if (activeTab === 'withdraw') {
                  handleWithdrawSearch(values);
                  return;
                }
                if (isAftersaleRefundTab) {
                  handleAftersaleSearch(values);
                  return;
                }
                handleOrderSearch(values);
              }}
            >
              <Form.Item
                label={activeTab === 'order' ? (
                  <Space size={4}>
                    <span>筛选时间</span>
                    <Tooltip title="美团订单按验券时间归属，微信订单按支付时间归属">
                      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                    </Tooltip>
                  </Space>
                ) : activeDateLabel}
              >
                <RangePicker
                  value={dateRange}
                  allowClear
                  onChange={dates => {
                    if (dates?.[0] && dates?.[1]) {
                      setDateRange([dates[0], dates[1]]);
                      setDateRangeExplicit(true);
                    } else {
                      setDateRange(null);
                      setDateRangeExplicit(false);
                    }
                  }}
                />
              </Form.Item>
              {isProviderFlowTab ? (
                <>
                  {activeTab === 'providerIncome' && (
                    <Form.Item name="orderNo" label="订单号">
                      <Input allowClear placeholder="请输入订单号 / 美团预约单号" style={{ width: 220 }} />
                    </Form.Item>
                  )}
                  <Form.Item name="providerName" label="代理商姓名">
                    <Input allowClear placeholder="请输入代理商姓名" style={{ width: 160 }} />
                  </Form.Item>
                  <Form.Item name="providerPhone" label="代理商电话">
                    <Input allowClear placeholder="请输入代理商电话" style={{ width: 170 }} />
                  </Form.Item>
                  {activeTab === 'providerIncome' && (
                    <Form.Item name="status" label="状态">
                      <Select
                        options={[
                          { label: '全部', value: '' },
                          { label: '待结算', value: 'pending' },
                          { label: '已结算', value: 'withdrawable' },
                        ]}
                        style={{ width: 150 }}
                      />
                    </Form.Item>
                  )}
                </>
              ) : isSettlementFlowTab ? (
                <>
                  <Form.Item name="orderNo" label="订单号">
                    <Input allowClear placeholder="请输入主订单号 / 子订单号" style={{ width: 220 }} />
                  </Form.Item>
                  <Form.Item name="title" label="标题">
                    <Input allowClear placeholder="请输入服务内容标题" style={{ width: 180 }} />
                  </Form.Item>
                  <Form.Item name="providerName" label="服务人员姓名">
                    <Input allowClear placeholder="请输入服务人员姓名" style={{ width: 160 }} />
                  </Form.Item>
                  <Form.Item name="providerPhone" label="服务人员电话">
                    <Input allowClear placeholder="请输入服务人员电话" style={{ width: 170 }} />
                  </Form.Item>
                  <Form.Item name="customerName" label="客户姓名">
                    <Input allowClear placeholder="请输入客户姓名" style={{ width: 160 }} />
                  </Form.Item>
                  <Form.Item name="customerPhone" label="客户电话">
                    <Input allowClear placeholder="请输入客户电话" style={{ width: 170 }} />
                  </Form.Item>
                </>
              ) : activeTab === 'deposit' ? (
                <>
                  <Form.Item name="status" label="状态">
                    <Select options={depositStatusOptions} style={{ width: 150 }} />
                  </Form.Item>
                  <Form.Item name="customerName" label="姓名">
                    <Input allowClear placeholder="请输入姓名" style={{ width: 160 }} />
                  </Form.Item>
                  <Form.Item name="customerPhone" label="电话">
                    <Input allowClear placeholder="请输入电话" style={{ width: 170 }} />
                  </Form.Item>
                </>
              ) : activeTab === 'order' ? (
                <>
                  <Form.Item name="orderSource" label="订单来源">
                    <Select
                      allowClear
                      options={orderSourceOptions}
                      style={{ width: 130 }}
                    />
                  </Form.Item>
                  <Form.Item name="verifiedStatus" label="核销状态">
                    <Select
                      allowClear
                      options={[
                        { label: '已核销', value: 'verified' },
                        { label: '未核销', value: 'unverified' },
                      ]}
                      placeholder="全部"
                      style={{ width: 140 }}
                    />
                  </Form.Item>
                  <Form.Item name="couponCode" label="券码">
                    <Input allowClear placeholder="请输入美团券码" style={{ width: 180 }} />
                  </Form.Item>
                  <Form.Item name="verifiedDateRange" label="验券日期">
                    <RangePicker allowClear />
                  </Form.Item>
                  <Form.Item name="paymentDateRange" label="支付时间">
                    <RangePicker allowClear />
                  </Form.Item>
                  <Form.Item name="fulfillmentDateRange" label="履约时间">
                    <RangePicker allowClear />
                  </Form.Item>
                  <Form.Item name="customerName" label="客户姓名">
                    <Input allowClear placeholder="请输入客户姓名" style={{ width: 160 }} />
                  </Form.Item>
                  <Form.Item name="customerPhone" label="客户电话">
                    <Input allowClear placeholder="请输入客户电话" style={{ width: 170 }} />
                  </Form.Item>
                  <Form.Item name="meituanReservationNo" label="美团预约单号">
                    <Input allowClear placeholder="请输入美团预约单号" style={{ width: 200 }} />
                  </Form.Item>
                  <Form.Item name="meituanOrderNo" label="美团订单号">
                    <Input allowClear placeholder="请输入美团订单号" style={{ width: 190 }} />
                  </Form.Item>
                  <Form.Item name="orderNo" label="订单号">
                    <Input allowClear placeholder="请输入系统订单号 / 支付单号" style={{ width: 220 }} />
                  </Form.Item>
                  <Form.Item name="creationModeGroup" label="录入方式">
                    <Select
                      allowClear
                      placeholder="全部"
                      style={{ width: 130 }}
                      options={[
                        { label: '后台手动', value: 'manual' },
                        { label: '自动进入', value: 'automatic' },
                        { label: '历史未知', value: 'unknown' },
                      ]}
                    />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item name="customerName" label={activeTab === 'withdraw' ? '服务人员姓名' : '客户姓名'}>
                    <Input
                      allowClear
                      placeholder={activeTab === 'withdraw' ? '请输入服务人员姓名' : '请输入客户姓名'}
                      style={{ width: 160 }}
                    />
                  </Form.Item>
                  <Form.Item name="customerPhone" label={activeTab === 'withdraw' ? '服务人员电话' : '客户电话'}>
                    <Input
                      allowClear
                      placeholder={activeTab === 'withdraw' ? '请输入服务人员电话' : '请输入客户电话'}
                      style={{ width: 170 }}
                    />
                  </Form.Item>
                </>
              )}
              {activeTab !== 'order' && activeTab !== 'withdraw' && activeTab !== 'deposit' && !isSettlementFlowTab && (
                <>
                  <Form.Item name="orderNo" label="订单号">
                    <Input
                      allowClear
                      placeholder={isAftersaleRefundTab ? '退款单号 / 业务订单号' : '支付单号 / 业务订单号'}
                      style={{ width: 220 }}
                    />
                  </Form.Item>
                </>
              )}
              <Form.Item>
                <Space>
                  <Button type="primary" icon={<SearchOutlined />} htmlType="submit">
                    查询
                  </Button>
                  <Button
                    onClick={handleActiveReset}
                  >
                    重置
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => {
                      if (recordsTruncated) {
                        message.warning(`当前筛选共 ${recordsTotal} 条，仅展示前 ${filteredRecords.length} 条；请缩小筛选范围后再导出完整数据`);
                        return;
                      }
                      setExportModalMode('export');
                      setExportModalOpen(true);
                    }}
                  >
                    导出
                  </Button>
                  {activeTab === 'order' && showMeituanStatementTools && (
                    <Dropdown
                      trigger={['click']}
                      menu={{
                        items: [
                          {
                            key: 'export-editable-ledger',
                            icon: <ExportOutlined />,
                            label: '导出可编辑券码账本',
                            onClick: () => void handleExportEditableMeituanSettlementLedger(),
                          },
                          {
                            key: 'import-editable-ledger',
                            icon: <ImportOutlined />,
                            label: (
                              <Upload
                                accept=".xlsx"
                                showUploadList={false}
                                beforeUpload={handleImportEditableMeituanSettlementLedger}
                                disabled={meituanSettlementImporting}
                              >
                                <span style={{ display: 'block', minWidth: 180 }}>导入可编辑券码账本</span>
                              </Upload>
                            ),
                          },
                          {
                            key: 'import-raw-statement',
                            icon: <ImportOutlined />,
                            label: (
                              <Upload
                                accept=".xlsx"
                                showUploadList={false}
                                beforeUpload={handleImportMeituanSettlement}
                                disabled={meituanSettlementImporting}
                              >
                                <span style={{ display: 'block', minWidth: 180 }}>导入美团原始收益账单</span>
                              </Upload>
                            ),
                          },
                        ],
                      }}
                    >
                      <Button icon={<ImportOutlined />} loading={meituanSettlementImporting}>
                        美团账单工具
                      </Button>
                    </Dropdown>
                  )}
                  <Button
                    onClick={() => {
                      setExportModalMode('config');
                      setExportModalOpen(true);
                    }}
                  >
                    导出配置
                  </Button>
                  <Button icon={<SettingOutlined />} onClick={() => setColumnConfigModalOpen(true)}>
                    列配置
                  </Button>
                  <Button icon={<BarChartOutlined />} onClick={() => setChartModalOpen(true)}>
                    图表查看
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          )}
          {recordsTruncated && (
            <div style={{ marginBottom: 12 }}>
              <Text type="warning">
                当前筛选共 {recordsTotal} 条，列表仅展示前 {filteredRecords.length} 条；汇总按全部结果计算。请缩小筛选范围后查看或导出完整明细。
              </Text>
            </div>
          )}
          <div ref={tableWrapRef}>
            <Table
              rowKey="id"
              loading={loading}
              columns={columns}
              dataSource={filteredRecords}
              scroll={{ x: tableScrollX }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: total => recordsTruncated
                  ? `当前已加载 ${total} 条，共 ${recordsTotal} 条（请缩小筛选范围）`
                  : `共 ${recordsTotal} 条`,
              }}
            />
          </div>
        </Card>
      </div>

      <Modal
        title="美团核销金额构成"
        open={meituanAmountModalOpen}
        onCancel={() => setMeituanAmountModalOpen(false)}
        footer={<Button type="primary" onClick={() => setMeituanAmountModalOpen(false)}>知道了</Button>}
        width={900}
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Card size="small" title="核销金额与结算" styles={{ body: { paddingBottom: 12 } }}>
            <Descriptions bordered size="small" column={2} styles={{ label: { width: 176, minWidth: 176, whiteSpace: 'nowrap' }, content: { minWidth: 0 } }}>
          {reportData?.summary.meituanStatementLoaded ? (
            <>
              <Descriptions.Item label="美团核销总收入">
                <Space orientation="vertical" size={2}>
                  <Text>{moneyFormatter(reportData.summary.meituanVerifiedGrossIncome)}</Text>
                  <Text type="secondary">收益明细表中已核销券码的“总收入”</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="账单商家营销费">
                <Space orientation="vertical" size={2}>
                  <Text>{moneyFormatter(reportData.summary.meituanStatementMarketingFee)}</Text>
                  <Text type="secondary">账单逐券费用，带正负号</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="核销收入（未扣技术费）" span={2}>
                {moneyFormatter(reportData.summary.meituanVerifiedGrossIncome)} + {moneyFormatter(reportData.summary.meituanStatementMarketingFee)} = <Text strong>{moneyFormatter(reportData.summary.meituanReconciledIncome)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="平台技术服务费">
                <Space orientation="vertical" size={2}>
                  <Text>{moneyFormatter(reportData.summary.meituanPlatformServiceFee)}</Text>
                  <Text type="secondary">收益明细表逐券费用</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="最终结算价" span={2}>
                <Space orientation="vertical" size={2}>
                  <Text strong>{moneyFormatter(reportData.summary.meituanStatementSettlementAmount)}</Text>
                  <Text type="secondary">直接取收益明细表结算价，不按固定费率推算</Text>
                </Space>
              </Descriptions.Item>
            </>
          ) : reportData?.summary.meituanPaymentShareDetailLoaded ? (
            <>
              <Descriptions.Item label="逐券有效分摊总额">
                <Space orientation="vertical" size={2}>
                  <Text>{moneyFormatter(reportData.summary.meituanSnapshotOrderTotalAmount)}</Text>
                  <Text type="secondary">仅汇总文档定义的分摊组成类型</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="逐券商家承担金额">
                <Space orientation="vertical" size={2}>
                  <Text>{moneyFormatter(reportData.summary.meituanCouponMerchantFundedAmount)}</Text>
                  <Text type="secondary">美团逐券分摊接口原值</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="核销金额" span={2}>
                {moneyFormatter(reportData.summary.meituanSnapshotOrderTotalAmount)} - {moneyFormatter(reportData.summary.meituanCouponMerchantFundedAmount)} = <Text strong>{moneyFormatter(reportData.summary.meituanVerifiedIncome)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="未知或汇总类型" span={2}>
                <Text type="secondary">分摊接口返回的验证汇总和未定义类型仅在分摊明细弹窗保留，<Text strong>不参与核销金额计算</Text>。</Text>
              </Descriptions.Item>
            </>
          ) : (
            <>
              <Descriptions.Item label="逐券有效分摊总额">
                <Space orientation="vertical" size={2}>
                  <Text>{moneyFormatter(reportData?.summary.meituanSnapshotOrderTotalAmount || 0)}</Text>
                  <Text type="secondary">仅汇总文档定义的分摊组成类型</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="已取得的商家承担金额">
                <Space orientation="vertical" size={2}>
                  <Text>{moneyFormatter(reportData?.summary.meituanCouponMerchantFundedAmount || 0)}</Text>
                  <Text type="secondary">已同步 <Text strong>{reportData?.summary.meituanPaymentShareMatchedCoupons || 0}/{reportData?.summary.meituanPaymentShareExpectedCoupons || 0}</Text> 张券</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="当前核销金额" span={2}>
                {moneyFormatter(reportData?.summary.meituanSnapshotOrderTotalAmount || 0)} - {moneyFormatter(reportData?.summary.meituanCouponMerchantFundedAmount || 0)} = <Text strong>{moneyFormatter(reportData?.summary.meituanVerifiedIncome || 0)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="未覆盖券说明" span={2}>
                <Text type="secondary">未取得逐券分摊的券已按订单查询金额计入，<Text strong>暂未扣除其商家承担金额</Text>；补齐后系统会自动重算。</Text>
              </Descriptions.Item>
            </>
          )}
            </Descriptions>
          </Card>

          <Card size="small" title="券码资金构成与订单参考" styles={{ body: { paddingBottom: 12 } }}>
            <Descriptions
              bordered
              size="small"
              column={2}
              styles={{
                label: { width: 176, minWidth: 176, whiteSpace: 'nowrap' },
                content: { minWidth: 0 },
              }}
            >
          {reportData?.summary && reportData.summary.meituanPaymentSnapshotMatchedCount > 0 ? (
            <>
              <Descriptions.Item label="核销券码美团支付">
                <Text style={{ whiteSpace: 'nowrap' }}>{moneyFormatter(reportData.summary.meituanSnapshotPaidAmount)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="核销券码平台抵用券">
                <Text style={{ whiteSpace: 'nowrap' }}>{moneyFormatter(reportData.summary.meituanCouponVoucherAmount)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="核销券码商户抵用券">
                <Text style={{ whiteSpace: 'nowrap' }}>{moneyFormatter(reportData.summary.meituanCouponMerchantVoucherAmount)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="核销券码分项合计" span={2}>
                <Space orientation="vertical" size={2} style={{ width: '100%' }}>
                  <Text style={{ whiteSpace: 'nowrap' }}>
                    {moneyFormatter(reportData.summary.meituanSnapshotPaidAmount)} + {moneyFormatter(reportData.summary.meituanCouponVoucherAmount)} + {moneyFormatter(reportData.summary.meituanCouponMerchantVoucherAmount)} = <Text strong>{moneyFormatter(reportData.summary.meituanSnapshotOrderTotalAmount)}</Text>
                  </Text>
                  <Text type="secondary">当前筛选的已核销券分项 · 已同步 <Text strong>{reportData.summary.meituanPaymentSnapshotMatchedCount}/{reportData.summary.meituanPaymentSnapshotExpectedCount}</Text> 个订单</Text>
                </Space>
              </Descriptions.Item>
              {reportData.summary.meituanPaymentShareDetailLoaded && (
                <>
                  <Descriptions.Item label="逐券平台承担金额">
                    <Text style={{ whiteSpace: 'nowrap' }}>{moneyFormatter(reportData.summary.meituanCouponPlatformFundedAmount)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="资金关系核对" span={2}>
                    <Space orientation="vertical" size={2} style={{ width: '100%' }}>
                      <Text style={{ whiteSpace: 'nowrap' }}>
                        {moneyFormatter(reportData.summary.meituanSnapshotPaidAmount)} + {moneyFormatter(reportData.summary.meituanCouponPlatformFundedAmount)} = <Text strong>{moneyFormatter(reportData.summary.meituanVerifiedIncome)}</Text>
                      </Text>
                      <Text type="secondary"><Text strong>未扣技术费</Text></Text>
                    </Space>
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="订单级金额参考" span={2}>
                <Text type="secondary"><Text strong>仅辅助核对，不参与核销金额。</Text> 按统一订单去重，可能包含当前筛选范围外的券码。</Text>
              </Descriptions.Item>
              <Descriptions.Item label="订单总金额（参考）">
                {moneyFormatter(reportData.summary.meituanOrderReferenceTotalAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="订单实付金额（参考）">
                {moneyFormatter(reportData.summary.meituanOrderReferencePaidAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="支付优惠金额（参考）">
                {moneyFormatter(reportData.summary.meituanOrderReferencePayDiscount)}
              </Descriptions.Item>
              <Descriptions.Item label="商家营销费（参考）">
                {moneyFormatter(reportData.summary.meituanOrderReferenceMerchantAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="平台分摊补贴（参考）">
                {moneyFormatter(reportData.summary.meituanOrderReferencePlatformAmount)}
              </Descriptions.Item>
            </>
          ) : (
            <Descriptions.Item label="订单详情快照" span={2}>
              <Text type="secondary">当前筛选范围没有已匹配的美团订单详情快照，订单总金额、实付、支付优惠、商家营销费和平台分摊补贴暂不汇总。</Text>
            </Descriptions.Item>
          )}
            </Descriptions>
          </Card>

          <Card size="small" title="补充收入" styles={{ body: { paddingBottom: 12 } }}>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="美团仅完成服务金额" span={2}>
                <Space orientation="vertical" size={2}>
                  <Text>{moneyFormatter(reportData?.summary.meituanCompletedOnlyIncome || 0)}</Text>
                  <Text type="secondary"><Text strong>{reportData?.summary.meituanCompletedOnlyOrderCount || 0} 笔</Text> · 无核销券码</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="当前美团收入" span={2}>
                <Space orientation="vertical" size={2}>
                  <Text strong>{moneyFormatter(reportData?.summary.meituanIncome || 0)}</Text>
                  <Text type="secondary">与“美团收入 / 核销金额”卡片一致</Text>
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Space>
      </Modal>
      <Modal
        title="美团核销数量与状态"
        open={meituanCouponStatsModalOpen}
        onCancel={() => setMeituanCouponStatsModalOpen(false)}
        footer={<Button type="primary" onClick={() => setMeituanCouponStatsModalOpen(false)}>知道了</Button>}
        width={720}
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Card size="small" title="券码数量" styles={{ body: { paddingBottom: 12 } }}>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="已核销券码">
                <Text strong>{reportData?.summary.verifiedCouponCount || 0}</Text> 张
              </Descriptions.Item>
              <Descriptions.Item label="未核销券码">
                <Text strong>{reportData?.summary.meituanUnverifiedCouponCount || 0}</Text> 张
              </Descriptions.Item>
              <Descriptions.Item label="部分核销订单中的已核销券">
                {reportData?.summary.meituanPartialVerifiedCouponCount || 0} 张
              </Descriptions.Item>
              <Descriptions.Item label="部分核销订单中的未核销券">
                {reportData?.summary.meituanPartialUnverifiedCouponCount || 0} 张
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card size="small" title="订单核销状态" styles={{ body: { paddingBottom: 12 } }}>
            <Descriptions bordered size="small" column={3}>
              <Descriptions.Item label="全部核销">
                <Text strong>{reportData?.summary.meituanFullyVerifiedOrderCount || 0}</Text> 单
              </Descriptions.Item>
              <Descriptions.Item label="部分核销">
                <Text strong>{reportData?.summary.meituanPartiallyVerifiedOrderCount || 0}</Text> 单
              </Descriptions.Item>
              <Descriptions.Item label="全部未核销">
                <Text strong>{reportData?.summary.meituanUnverifiedOrderCount || 0}</Text> 单
              </Descriptions.Item>
              <Descriptions.Item label="券码未同步订单" span={3}>
                {reportData?.summary.meituanCouponUnknownOrderCount || 0} 单。此类订单没有完整券码明细，无法判断核销状态，不纳入已核销或未核销券码数量。
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card size="small" title="统计规则" styles={{ body: { paddingBottom: 12 } }}>
            <Text type="secondary">
              自动美团仅统计已支付、未取消订单，并按验券时间归属；手动美团不进入核销券码数量统计。验券日期、支付日期、履约日期均为独立附加筛选条件。
            </Text>
          </Card>
        </Space>
      </Modal>
      {isOrderIncomeTab ? (
        <Modal
          title="订单详情"
          open={orderDetailModalOpen}
          onCancel={() => setOrderDetailModalOpen(false)}
          footer={null}
          width={920}
          destroyOnHidden
        >
          <Spin spinning={orderDetailLoading}>
            <Space orientation="vertical" size={16} style={{ width: '100%' }}>
              <Text strong>订单信息</Text>
              <Descriptions bordered size="small" column={2} styles={{ label: { width: 112, minWidth: 112, whiteSpace: 'nowrap' } }}>
              <Descriptions.Item label="订单号">{orderDetailRecord?.relatedOrderNo || orderDetailResult?.orderNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="订单来源">{orderDetailRecord?.orderSource || orderDetailResult?.source || '-'}</Descriptions.Item>
              <Descriptions.Item label="订单状态">{orderDetailRecord?.orderStatus || orderDetailResult?.statusText || orderDetailResult?.status || '-'}</Descriptions.Item>
              <Descriptions.Item label="支付状态">{orderDetailRecord?.payStatus || orderDetailResult?.payStatus || '-'}</Descriptions.Item>
              <Descriptions.Item label="客户姓名">{orderDetailRecord?.customerName || orderDetailResult?.contactName || '-'}</Descriptions.Item>
              <Descriptions.Item label="联系电话">{orderDetailRecord?.customerPhone || orderDetailResult?.contactPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="服务项目">{orderDetailRecord?.productName || orderDetailResult?.productName || '-'}</Descriptions.Item>
              <Descriptions.Item label="宠物">{orderDetailRecord?.petName || orderDetailResult?.petName || '-'}</Descriptions.Item>
              <Descriptions.Item label="服务时间" span={2}>
                {orderDetailRecord?.serviceDate || orderDetailResult?.serviceDateStart || '-'}
                {orderDetailRecord?.serviceTimeDesc || orderDetailResult?.serviceTimeDesc ? ` ${orderDetailRecord?.serviceTimeDesc || orderDetailResult?.serviceTimeDesc}` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="服务人员">{orderDetailRecord?.assignedStaffName || orderDetailResult?.assignedStaffName || '-'}</Descriptions.Item>
              <Descriptions.Item label="履约完成时间">{orderDetailRecord?.fulfillmentCompletedAt || orderDetailResult?.fulfillmentCompletedAt || '-'}</Descriptions.Item>
              {isOrderDetailMeituan && (
                <>
                  <Descriptions.Item label="美团订单号">{orderDetailRecord?.meituanOrderNo || orderDetailResult?.externalOrderId || '-'}</Descriptions.Item>
                  <Descriptions.Item label="美团统一订单号">{orderDetailRecord?.meituanReservationNo || orderDetailResult?.externalReservationNo || '-'}</Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="服务地址" span={2}>{orderDetailRecord?.addressText || orderDetailResult?.addressText || '-'}</Descriptions.Item>
              </Descriptions>

              <Text strong>财务信息</Text>
              <Descriptions bordered size="small" column={2} styles={{ label: { width: 128, minWidth: 128, whiteSpace: 'nowrap' } }}>
              <Descriptions.Item label="本次收入">{moneyFormatter(orderDetailRecord?.amount || 0)}</Descriptions.Item>
              <Descriptions.Item label="订单总金额">{moneyFormatter(orderDetailRecord?.totalAmount || orderDetailResult?.totalAmount || 0)}</Descriptions.Item>
              <Descriptions.Item label="订单单价">{moneyFormatter(orderDetailRecord?.unitPrice || orderDetailResult?.unitPrice || 0)}</Descriptions.Item>
              <Descriptions.Item label="支付方式">{orderDetailRecord?.payMethod || orderDetailResult?.payMethod || '-'}</Descriptions.Item>
              <Descriptions.Item label="支付成功时间">{orderDetailResult?.paidAt || orderDetailRecord?.occurredAt || '-'}</Descriptions.Item>
              <Descriptions.Item label="支付单号">{orderDetailRecord?.paymentOrderNo || '-'}</Descriptions.Item>
              {isOrderDetailMeituan && (
                <>
                  <Descriptions.Item label="财务发生时间">{orderDetailRecord?.occurredAt || '-'}</Descriptions.Item>
                  <Descriptions.Item label="收入金额来源">{orderDetailRecord?.meituanIncomeAmountSource || '-'}</Descriptions.Item>
                  <Descriptions.Item label="美团订单实付">{moneyFormatter(orderDetailRecord?.meituanOrderPaidAmount ?? 0)}</Descriptions.Item>
                  <Descriptions.Item label="支付优惠">{moneyFormatter(orderDetailRecord?.meituanPayDiscountAmount ?? 0)}</Descriptions.Item>
                  <Descriptions.Item label="核销金额（分摊净额）">{moneyFormatter(orderDetailRecord?.meituanVerifiedAmount || orderDetailResult?.meituanVerifiedAmount || 0)}</Descriptions.Item>
                  <Descriptions.Item label="核销金额（分摊原额）">{moneyFormatter(orderDetailRecord?.meituanVerifiedGrossAmount || 0)}</Descriptions.Item>
                  <Descriptions.Item label="商家承担（分摊）">{moneyFormatter(orderDetailRecord?.meituanMerchantFundedAmount || 0)}</Descriptions.Item>
                  <Descriptions.Item label="核销金额来源">{orderDetailRecord?.meituanVerifiedAmountSource || '-'}</Descriptions.Item>
                  <Descriptions.Item label="逐券分摊覆盖">{orderDetailRecord?.meituanVerifiedCoverage || '-'}</Descriptions.Item>
                  <Descriptions.Item label="账单商家营销费（逐券汇总）">{moneyFormatter(orderDetailRecord?.meituanStatementMarketingFee ?? 0)}</Descriptions.Item>
                  <Descriptions.Item label="订单级商家营销费（参考）">{moneyFormatter(orderDetailRecord?.meituanMerchantMarketingAmount ?? orderDetailResult?.meituanMerchantMarketingAmount ?? 0)}</Descriptions.Item>
                  <Descriptions.Item label="平台分摊补贴">{moneyFormatter(orderDetailRecord?.meituanPlatformSubsidy ?? 0)}</Descriptions.Item>
                  <Descriptions.Item label="平台技术服务费">{moneyFormatter(orderDetailRecord?.meituanPlatformServiceFee ?? 0)}</Descriptions.Item>
                  <Descriptions.Item label="美团结算金额">{moneyFormatter(orderDetailRecord?.meituanSettlementAmount ?? 0)}</Descriptions.Item>
                </>
              )}
              </Descriptions>
            </Space>
          </Spin>
        </Modal>
      ) : null}
      <Modal
        title="美团分摊明细"
        open={meituanPaymentSharesModalOpen}
        onCancel={() => setMeituanPaymentSharesModalOpen(false)}
        footer={null}
        width={820}
        destroyOnHidden
      >
        <Space orientation="vertical" size={14} style={{ width: '100%' }}>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="平台订单号">
              {meituanPaymentSharesResult?.serviceOrderNo || meituanPaymentSharesRecord?.relatedOrderNo || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="美团订单ID">
              {meituanPaymentSharesResult?.orderId || meituanPaymentSharesRecord?.meituanReservationNo || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="TraceID" span={2}>
              {meituanPaymentSharesResult?.traceId || '-'}
            </Descriptions.Item>
          </Descriptions>
          <Space align="center" wrap>
            <Text strong>
              {meituanPaymentShareGroups.primary.length > 0 ? `当前子订单核销券码分摊（${meituanPaymentShareGroups.primary.length} 条）` : '分摊券码'}
            </Text>
            {currentSuborderCouponCodes.length > 0 ? (
              <Text type="secondary">券码：{currentSuborderCouponCodes.join('、')}</Text>
            ) : null}
          </Space>
          <Table
            rowKey={record => `${record.receiptCode || 'receipt'}-${record.amountType || 0}-${record.amount || 0}`}
            size="small"
            loading={meituanPaymentSharesLoading}
            dataSource={meituanPaymentShareGroups.primary}
            pagination={false}
            locale={{ emptyText: meituanPaymentSharesLoading ? '查询中' : '暂无分摊明细数据' }}
            columns={meituanPaymentShareColumns}
          />
          {meituanPaymentShareGroups.rest.length > 0 ? (
            <Collapse
              items={[
                {
                  key: 'other-payment-shares',
                  label: `所属美团订单其他券码（默认折叠，${meituanPaymentShareGroups.rest.length} 条）`,
                  children: (
                    <Table
                      rowKey={record => `${record.receiptCode || 'receipt'}-${record.amountType || 0}-${record.amount || 0}`}
                      size="small"
                      dataSource={meituanPaymentShareGroups.rest}
                      pagination={false}
                      columns={meituanPaymentShareColumns}
                    />
                  ),
                },
              ]}
            />
          ) : null}
        </Space>
      </Modal>
      <Modal
        title="统计口径说明"
        open={statisticsRuleModalOpen}
        onCancel={() => setStatisticsRuleModalOpen(false)}
        footer={<Button type="primary" onClick={() => setStatisticsRuleModalOpen(false)}>知道了</Button>}
        width={720}
      >
        {isOrderIncomeTab ? (
          <Space orientation="vertical" size={12} style={{ width: '100%' }}>
            <Paragraph>
              当前为<strong>订单收入</strong>统计视图。日期范围按各渠道的业务确认时间归属，不以订单创建时间统计。
            </Paragraph>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="收入合计">
                美团自动订单核销金额、后台手动美团对公收款、后台手动微信收款和微信小程序收款之和；同一订单只归入一个创建方式分类。
              </Descriptions.Item>
              <Descriptions.Item label="订单数合计">
                纳入收入合计的美团订单数，加后台人工微信收款订单数；美团多券订单只按一个订单计算。
              </Descriptions.Item>
              <Descriptions.Item label="美团收入 / 核销金额">
                卡片始终显示当前筛选结果：未选择核销状态时为全部美团收入；选择已核销时为已核销券码的逐券有效分摊金额；选择未核销时为未核销订单金额。状态资格固定为已支付且未取消。顶部筛选时间是主统计时间窗口：自动美团按验券时间归属，手动美团按支付时间（缺失时按录入时间）归属，微信按支付成功时间归属；清空后不追加主时间条件。验券日期仅筛选自动美团核销时间，支付时间仅筛选支付成功时间，履约时间仅筛选履约完成时间；填写多个时间条件时取交集。逐券有效分摊金额为逐券分摊组成合计减逐券商家承担金额，不使用统一美团订单总额。
              </Descriptions.Item>
              <Descriptions.Item label="列表核销金额">
                仅累计当前子订单已核销券码的逐券分摊：分摊原额减去逐券商家承担，得到“核销金额（分摊净额）”；不会累计同一美团统一订单下其他子订单的券码。逐券分摊未完整同步时保留订单券码参考值，并通过“核销金额来源”和“逐券分摊覆盖”标识。
              </Descriptions.Item>
              <Descriptions.Item label="美团核销码数">
                按 `coupon_info` 中已核销券码统计；多券订单会按真实券码数计算。金额卡片和核销码数卡片上的信息按钮可查看全核销、部分核销、全未核销及券码未同步的数量。
              </Descriptions.Item>
              <Descriptions.Item label="微信收款金额 / 笔数">
                仅统计 `creation_mode=admin_manual` 的微信订单；历史兼容仍参考 `admin_create_wechat` 账号标识。已支付且未取消，按支付成功时间归属。
              </Descriptions.Item>
              <Descriptions.Item label="小程序收款">
                统计 `creation_mode=wechat_app` 的微信订单，按微信支付成功时间归属；历史 `unknown` 且不是后台手动账号的微信订单按小程序兼容口径统计。
              </Descriptions.Item>
              <Descriptions.Item label="对公收款">
                统计 `source=meituan AND creation_mode=admin_manual` 的美团订单，按后台录入时生成的支付时间归属，不进入美团核销卡片。
              </Descriptions.Item>
            </Descriptions>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              美团收益账单中的入账日期、打款状态仅用于后续到账对账，不影响当前“核销 / 微信支付”统计。
            </Paragraph>
          </Space>
        ) : (
          <Paragraph style={{ marginBottom: 0 }}>
            各卡片按当前日期范围和页面筛选条件统计；订单收入以支付或业务完成时间归属，退款以退款成功时间归属，其余流水以各自发生或结算时间归属。
          </Paragraph>
        )}
      </Modal>

      <Modal
        title="美团券码信息"
        open={meituanCouponModalOpen}
        onCancel={() => setMeituanCouponModalOpen(false)}
        footer={null}
        width={980}
        destroyOnHidden
      >
        <Space orientation="vertical" size={14} style={{ width: '100%' }}>
          <Card loading={meituanCouponLoading} size="small">
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="平台订单号">
                {meituanCouponResult?.serviceOrderNo || meituanCouponRecord?.relatedOrderNo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="美团订单号">
                {meituanCouponResult?.unifiedOrderId || meituanCouponRecord?.meituanOrderNo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="预约单号">
                {meituanCouponRecord?.meituanReservationNo || meituanCouponResult?.orderId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="订单金额">
                {formatMeituanAmountText(meituanCouponResult?.order?.amount ?? meituanCouponResult?.amount)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card loading={meituanCouponLoading} size="small" title="订单响应信息">
            <Descriptions column={3} size="small" bordered>
              <Descriptions.Item label="统一订单ID">
                {formatMeituanValueText(meituanCouponOrder.unifiedOrderId || meituanCouponResult?.unifiedOrderId)}
              </Descriptions.Item>
              <Descriptions.Item label="订单ID">
                {formatMeituanValueText(meituanCouponOrder.orderId || meituanCouponResult?.orderId)}
              </Descriptions.Item>
              <Descriptions.Item label="订单状态">
                {(() => {
                  const status = Number(meituanCouponOrder.status);
                  const config = meituanOrderStatusMap[status];
                  return config ? <Tag color={config.color}>{config.text}</Tag> : formatMeituanValueText(meituanCouponOrder.status);
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="订单创建时间">
                {formatMeituanTimestampText(meituanCouponOrder.addTime)}
              </Descriptions.Item>
              <Descriptions.Item label="支付成功时间">
                {formatMeituanTimestampText(meituanCouponOrder.paySuccessTime)}
              </Descriptions.Item>
              <Descriptions.Item label="订单渠道">
                {meituanOrderChannelMap[String(meituanCouponOrder.orderChannel || '')] || formatMeituanValueText(meituanCouponOrder.orderChannel)}
              </Descriptions.Item>
              <Descriptions.Item label="订单总金额">
                {formatMeituanAmountText(meituanCouponOrder.amount)}
              </Descriptions.Item>
              <Descriptions.Item label="订单实付金额">
                {formatMeituanAmountText(meituanCouponOrder.paidAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="支付优惠金额">
                {formatMeituanAmountText(meituanCouponOrder.payDiscountAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="商家营销费">
                {formatMeituanAmountText(meituanCouponOrder.merchantAmount)}
              </Descriptions.Item>

              <Descriptions.Item label="平台分摊补贴">
                {formatMeituanAmountText(meituanCouponOrder.platformAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="业务类型">
                {formatMeituanValueText(meituanCouponOrder.bizType)}
              </Descriptions.Item>
              <Descriptions.Item label="门店ID">
                {formatMeituanValueText(meituanCouponOrder.opPoiId)}
              </Descriptions.Item>
              <Descriptions.Item label="商品类型">
                {formatMeituanValueText(meituanCouponOrder.spuType)}
              </Descriptions.Item>
              <Descriptions.Item label="后订订单">
                {formatMeituanValueText(meituanCouponOrder.bookAfterGroup)}
              </Descriptions.Item>
              <Descriptions.Item label="预订开始时间">
                {formatMeituanTimestampText(meituanCouponOrder.bookStartTime)}
              </Descriptions.Item>
              <Descriptions.Item label="预订结束时间">
                {formatMeituanTimestampText(meituanCouponOrder.bookEndTime)}
              </Descriptions.Item>
              <Descriptions.Item label="traceId">
                {formatMeituanValueText(meituanCouponResult?.traceId)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Table<MeituanOrderQueryInfoReceipt>
            rowKey={record => `${record.serialNumber || record.receiptCode || 'receipt'}-${record.receiptStatus || record.status || 0}-${record.receiptBeginTime || ''}`}
            size="small"
            title={() => '商品券码信息'}
            loading={meituanCouponLoading}
            dataSource={meituanCouponReceipts}
            pagination={false}
            locale={{ emptyText: meituanCouponLoading ? '查询中' : '暂无商品券码信息' }}
            columns={[
              {
                title: '券码',
                dataIndex: 'serialNumber',
                width: 240,
                render: (_value: string, record) => record.serialNumber || record.receiptCode || '-',
              },
              {
                title: '核销状态',
                dataIndex: 'receiptStatus',
                width: 180,
                render: (_value: number, record) => {
                  const status = Number(record.receiptStatus ?? record.status ?? 0);
                  const config = meituanReceiptStatusMap[status] || { color: 'default', text: status ? String(status) : '-' };
                  return <Tag color={config.color}>{config.text}</Tag>;
                },
              },
              {
                title: '可用开始时间',
                dataIndex: 'receiptBeginTime',
                width: 220,
                render: (value: number) => formatMeituanTimestampText(value),
              },
              {
                title: '可用截止时间',
                dataIndex: 'receiptEndTime',
                width: 220,
                render: (value: number) => formatMeituanTimestampText(value),
              },
              {
                title: '关联预订',
                dataIndex: 'relatedBook',
                width: 120,
                render: (value: boolean) => formatMeituanValueText(value),
              },
            ]}
          />
          <Table<Record<string, unknown>>
            rowKey={record => `${String(record.title || 'amount-share')}-${String(record.amountType || '')}-${String(record.value ?? record.amount ?? '')}`}
            size="small"
            title={() => '券码金额分摊信息'}
            loading={meituanCouponLoading}
            dataSource={meituanCouponReceipts.flatMap(receipt =>
              Array.isArray(receipt.amountShareList)
                ? receipt.amountShareList.map(item => ({
                    ...item,
                    receiptCode: receipt.serialNumber || receipt.receiptCode,
                  }))
                : [],
            )}
            pagination={false}
            locale={{ emptyText: meituanCouponLoading ? '查询中' : '暂无券码金额分摊信息' }}
            columns={[
              {
                title: '券码',
                dataIndex: 'receiptCode',
                width: 220,
                render: (value: unknown) => formatMeituanValueText(value),
              },
              {
                title: '金额类型',
                dataIndex: 'amountType',
                width: 140,
                render: (value: unknown) => meituanAmountShareTypeMap[Number(value)] || formatMeituanValueText(value),
              },
              {
                title: '名称',
                dataIndex: 'title',
                width: 180,
                render: (value: unknown) => formatMeituanValueText(value),
              },
              {
                title: '金额/值',
                dataIndex: 'value',
                width: 160,
                render: (value: unknown) => formatMeituanFenAmountText(value),
              },
            ]}
          />
          <Table<Record<string, unknown>>
            rowKey={record => `${String(record.payBatchNo || 'pre-amount')}-${String(record.amountType || '')}-${String(record.amount ?? '')}`}
            size="small"
            title={() => '订单下单金额明细快照'}
            loading={meituanCouponLoading}
            dataSource={meituanCouponOrderPreAmountList}
            pagination={false}
            locale={{ emptyText: meituanCouponLoading ? '查询中' : '暂无订单下单金额明细' }}
            columns={[
              {
                title: '支付批次',
                dataIndex: 'payBatchNo',
                width: 140,
                render: (value: unknown) => formatMeituanValueText(value),
              },
              {
                title: '支付类型',
                dataIndex: 'amountType',
                width: 140,
                render: (value: unknown) => meituanAmountShareTypeMap[Number(value)] || formatMeituanValueText(value),
              },
              {
                title: '支付金额',
                dataIndex: 'amount',
                width: 160,
                render: (value: unknown) => formatMeituanFenAmountText(value),
              },
            ]}
          />
          <Table<Record<string, unknown>>
            rowKey={record => `${String(record.applyId || 'refund')}-${String(record.refundStatus || record.status || '')}-${String(record.refundSuccessTime || '')}`}
            size="small"
            title={() => '退款申请单列表'}
            loading={meituanCouponLoading}
            dataSource={meituanCouponRefundApplicationList}
            pagination={false}
            locale={{ emptyText: meituanCouponLoading ? '查询中' : '暂无退款申请单' }}
            columns={[
              {
                title: '退款单ID',
                dataIndex: 'applyId',
                width: 180,
                render: (value: unknown) => formatMeituanValueText(value),
              },
              {
                title: '退款状态',
                dataIndex: 'applyStatus',
                width: 160,
                render: (value: unknown) => formatMeituanValueText(value),
              },
              {
                title: '实际退款金额',
                dataIndex: 'refundAmount',
                width: 160,
                render: (value: unknown) => formatMeituanAmountText(value),
              },
              {
                title: '退款完成时间',
                dataIndex: 'refundSuccessTime',
                width: 220,
                render: (value: unknown) => formatMeituanTimestampText(value),
              },
            ]}
          />
          <Table<MeituanOrderQueryInfoExtraPayment>
            rowKey={record => `${record.serialNumber || 'extra-payment'}-${record.paymentType || 0}-${record.amount || 0}`}
            size="small"
            title={() => '其他费用信息'}
            loading={meituanCouponLoading}
            dataSource={meituanCouponExtraPayments}
            pagination={false}
            locale={{ emptyText: meituanCouponLoading ? '查询中' : '暂无其他费用信息' }}
            columns={[
              {
                title: '费用类型',
                dataIndex: 'paymentType',
                width: 160,
                render: (value: number) => meituanExtraPaymentTypeMap[Number(value || 0)] || value || '-',
              },
              {
                title: '费用金额',
                dataIndex: 'amount',
                width: 140,
                render: (value: string) => formatMeituanAmountText(value),
              },
              {
                title: '支付状态',
                dataIndex: 'payStatus',
                width: 140,
                render: (value: number) => {
                  const config = meituanExtraPaymentStatusMap[Number(value ?? -1)] || { color: 'default', text: value === undefined ? '-' : String(value) };
                  return <Tag color={config.color}>{config.text}</Tag>;
                },
              },
              {
                title: '核销状态',
                dataIndex: 'receiptStatus',
                width: 180,
                render: (value: number) => {
                  const config = meituanReceiptStatusMap[Number(value ?? 0)] || { color: 'default', text: value === undefined ? '-' : String(value) };
                  return <Tag color={config.color}>{config.text}</Tag>;
                },
              },
              {
                title: '券码',
                dataIndex: 'serialNumber',
                width: 240,
                render: (value: string) => value || '-',
              },
            ]}
          />
        </Space>
      </Modal>
      <Modal
        title={exportModalMode === 'config' ? `${activeReportLabel}导出配置` : `导出${activeReportLabel}`}
        open={exportModalOpen}
        onCancel={() => setExportModalOpen(false)}
        onOk={handleExportFinanceRecords}
        okText={exportModalMode === 'config' ? '保存配置' : '导出'}
        cancelText="取消"
        confirmLoading={exportConfigLoading}
        width={680}
      >
        <Paragraph type="secondary">
          {exportModalMode === 'config'
            ? '保存默认导出字段、字段顺序和默认导出条数，下次进入该页面会自动使用。'
            : '选择需要导出的字段，并通过上下按钮调整导出顺序。导出数据为当前完整查询结果。'}
        </Paragraph>
        <Form layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item label="导出条数">
            <InputNumber
              min={1}
              precision={0}
              value={exportLimit}
              placeholder={`全部 ${filteredRecords.length} 条`}
              onChange={value => setExportLimit(typeof value === 'number' ? value : null)}
              style={{ width: 180 }}
            />
          </Form.Item>
          <Form.Item>
            <Text type="secondary">不填写则导出当前查询结果全部 {filteredRecords.length} 条。</Text>
          </Form.Item>
        </Form>
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <Text strong>已导出字段</Text>
            <Text type="secondary" style={{ marginLeft: 8 }}>
              按下面顺序生成 Excel 列
            </Text>
          </div>
          <Space orientation="vertical" style={{ width: '100%' }} size={8}>
            {selectedExportFields.map((field, index) => (
                <div
                  key={field.key}
                  onDragOver={event => handleExportDragOver(event, field.key)}
                  onDrop={handleExportDragEnd}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '8px 12px',
                    border: '1px solid #d9e8ff',
                    borderRadius: 8,
                    background: draggingExportKey === field.key ? '#eaf4ff' : '#f7fbff',
                    boxShadow: '0 1px 2px rgba(22, 119, 255, 0.08)',
                    opacity: draggingExportKey === field.key ? 0.72 : 1,
                  }}
                >
                  <Space size={10}>
                    <span
                      draggable
                      title="按住拖动排序"
                      onDragStart={event => handleExportDragStart(event, field.key)}
                      onDragEnd={handleExportDragEnd}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: '#8c8c8c',
                        cursor: 'grab',
                        fontSize: 16,
                      }}
                    >
                      <HolderOutlined />
                    </span>
                    <Checkbox checked onChange={event => handleExportFieldCheck(field.key, event.target.checked)}>
                      {getFinanceDetailFieldLabel(field, activeTab)}
                    </Checkbox>
                  </Space>
                  <Space>
                    <Tag color="blue">第 {index + 1} 列</Tag>
                    <Button
                      size="small"
                      icon={<MoveUpOutlined />}
                      disabled={index <= 0}
                      onClick={() => moveExportField(field.key, 'up')}
                    />
                    <Button
                      size="small"
                      icon={<MoveDownOutlined />}
                      disabled={index >= selectedExportFields.length - 1}
                      onClick={() => moveExportField(field.key, 'down')}
                    />
                  </Space>
                </div>
              ))}
          </Space>

          {unselectedExportFields.length > 0 && (
            <>
              <div style={{ marginTop: 8 }}>
                <Text strong>可选字段</Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  勾选后会追加到已导出字段末尾
                </Text>
              </div>
              <Space orientation="vertical" style={{ width: '100%' }} size={8}>
                {unselectedExportFields.map(field => (
                  <div
                    key={field.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      background: '#fafafa',
                    }}
                  >
                    <Checkbox checked={false} onChange={event => handleExportFieldCheck(field.key, event.target.checked)}>
                      {getFinanceDetailFieldLabel(field, activeTab)}
                    </Checkbox>
                    <Text type="secondary">未导出</Text>
                  </div>
                ))}
              </Space>
            </>
          )}
        </Space>
      </Modal>

      <Modal
        title={`${activeReportLabel}列配置`}
        open={columnConfigModalOpen}
        onCancel={() => setColumnConfigModalOpen(false)}
        onOk={handleSaveColumnConfig}
        okText="保存配置"
        cancelText="取消"
        confirmLoading={columnConfigLoading}
        width={980}
      >
        <Paragraph type="secondary">
          选择{activeReportLabel}列表需要展示的字段，并配置每列宽度、高度和显示顺序。未配置宽高时使用当前默认值。
        </Paragraph>
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <Text strong>已显示字段</Text>
            <Text type="secondary" style={{ marginLeft: 8 }}>
              宽高留空则使用当前默认值
            </Text>
          </div>
          <Space orientation="vertical" style={{ width: '100%' }} size={8}>
            {selectedColumnFields.map((field, index) => (
              <div
                key={field.key}
                onDragOver={event => handleColumnDragOver(event, field.key)}
                onDrop={handleColumnDragEnd}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '8px 12px',
                  border: '1px solid #d9e8ff',
                  borderRadius: 8,
                  background: draggingColumnKey === field.key ? '#eaf4ff' : '#f7fbff',
                  boxShadow: '0 1px 2px rgba(22, 119, 255, 0.08)',
                  opacity: draggingColumnKey === field.key ? 0.72 : 1,
                }}
              >
                <Space size={10}>
                  <span
                    draggable
                    title="按住拖动排序"
                    onDragStart={event => handleColumnDragStart(event, field.key)}
                    onDragEnd={handleColumnDragEnd}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: '#8c8c8c',
                      cursor: 'grab',
                      fontSize: 16,
                    }}
                  >
                    <HolderOutlined />
                  </span>
                  <Checkbox checked onChange={event => handleColumnFieldCheck(field.key, event.target.checked)}>
                    {getFinanceDetailFieldLabel(field, activeTab)}
                  </Checkbox>
                </Space>
                <Space wrap>
                  <InputNumber
                    min={40}
                    precision={0}
                    addonBefore="宽"
                    addonAfter="px"
                    placeholder={`${financeDetailColumnWidthMap[field.key] || 150}`}
                    value={columnSizes[field.key]?.width}
                    onChange={value =>
                      setColumnSizes(prev => ({
                        ...prev,
                        [field.key]: {
                          ...prev[field.key],
                          width: typeof value === 'number' && value > 0 ? value : undefined,
                        },
                      }))
                    }
                    style={{ width: 150 }}
                  />
                  <InputNumber
                    min={24}
                    precision={0}
                    addonBefore="高"
                    addonAfter="px"
                    placeholder="默认"
                    value={columnSizes[field.key]?.height}
                    onChange={value =>
                      setColumnSizes(prev => ({
                        ...prev,
                        [field.key]: {
                          ...prev[field.key],
                          height: typeof value === 'number' && value > 0 ? value : undefined,
                        },
                      }))
                    }
                    style={{ width: 150 }}
                  />
                  <Button
                    size="small"
                    onClick={() =>
                      setColumnSizes(prev => {
                        const next = { ...prev };
                        delete next[field.key];
                        return next;
                      })
                    }
                  >
                    默认
                  </Button>
                  <Tag color="blue">第 {index + 1} 列</Tag>
                  <Button
                    size="small"
                    icon={<MoveUpOutlined />}
                    disabled={index <= 0}
                    onClick={() => moveColumnField(field.key, 'up')}
                  />
                  <Button
                    size="small"
                    icon={<MoveDownOutlined />}
                    disabled={index >= selectedColumnFields.length - 1}
                    onClick={() => moveColumnField(field.key, 'down')}
                  />
                </Space>
              </div>
            ))}
          </Space>

          {unselectedColumnFields.length > 0 && (
            <>
              <div style={{ marginTop: 8 }}>
                <Text strong>可选字段</Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  勾选后会追加到已显示字段末尾
                </Text>
              </div>
              <Space orientation="vertical" style={{ width: '100%' }} size={8}>
                {unselectedColumnFields.map(field => (
                  <div
                    key={field.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      background: '#fafafa',
                    }}
                  >
                    <Checkbox checked={false} onChange={event => handleColumnFieldCheck(field.key, event.target.checked)}>
                      {getFinanceDetailFieldLabel(field, activeTab)}
                    </Checkbox>
                    <Text type="secondary">未显示</Text>
                  </div>
                ))}
              </Space>
            </>
          )}
        </Space>
      </Modal>

      <Modal
        title={`${activeReportLabel}图表`}
        open={chartModalOpen}
        onCancel={() => setChartModalOpen(false)}
        footer={null}
        width={1080}
      >
        <Paragraph type="secondary">
          当前图表基于{activeReportLabel}明细的查询结果生成，共 {filteredRecords.length} 条{activeReportLabel}数据。
        </Paragraph>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card size="small" title={`${activeReportLabel}趋势折线图`}>
              <ReactECharts option={orderIncomeLineOption} style={{ height: 320 }} />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card size="small" title={`每日${activeReportLabel}金额与数量`}>
              <ReactECharts option={orderIncomeBarOption} style={{ height: 320 }} />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card size="small" title={activeTab === 'aftersale' ? '退款渠道金额与状态占比' : '渠道金额与状态占比'}>
              <ReactECharts option={orderIncomePieOption} style={{ height: 320 }} />
            </Card>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default FinanceReport;






